import { getDiffText } from '../../utils/diff_text';
import { safeSanitize } from '../../utils/safe_sanitize';

angular.module('LeasePilot').directive('playbookEditor', [
  'ApiService',
  'AiServicesConsentService',
  '$interval',
  '$timeout',
  function(ApiService, AiServicesConsentService, $interval, $timeout) {
    return {
      restrict: 'E',
      templateUrl: 'playbook_editor.html',
      scope: {},
      link: function(scope, element) {
        const root = element[0];

        // Delegated, not bound to the input itself. There are now two file
        // inputs — one on the start plate, one in the chrome that replaces it —
        // and each lives inside an ng-if, so at link time NEITHER exists.
        // `querySelector(...).addEventListener` threw on null here and killed
        // the whole directive before its first load(), which showed up as a
        // screen stuck on "Loading playbook…" with no network request behind
        // it. Delegation also means the handler survives the inputs being
        // created and destroyed as the screen advances.
        const findFileInput = () => root.querySelector('[data-playbook-files]');

        scope.documentTypes = [];
        scope.vocabulary = [];
        scope.entries = [];
        scope.historyByConcept = {};
        scope.files = [];
        // Which zone a file is currently over, by id — see handleDragOver.
        scope.droppingOn = null;
        scope.dropError = null;
        scope.documentTypeLabels = {};
        scope.escalationOwners = [];
        scope.loading = true;
        scope.refreshing = false;
        // Read once. The restored notice says how the reviewer GOT here, which
        // is a different question from what is left to do, and it is the only
        // one of the three status blocks with an answer that stops mattering.
        scope.draftNoticeDismissed = false;
        scope.importing = false;
        scope.saving = false;
        // How long the current long read has been running, in seconds.
        //
        // A spinner answers "is anything still happening". Past about a minute
        // the question becomes "how long has this been going", and only a
        // number answers that — the same reason the deal-terms import card grew
        // one. Null whenever nothing is running, so the template binds it
        // directly.
        scope.elapsedSeconds = null;
        let elapsedHandle = null;
        let elapsedFrom = 0;

        // Guarded the way `showActiveFromTheTop` guards `$timeout`, and for the
        // same reason: a clock is an enhancement on top of a read, and a
        // missing one must not be able to stop the read happening.
        function startElapsed() {
          stopElapsed();
          if (typeof $interval !== 'function') return;

          elapsedFrom = Date.now();
          scope.elapsedSeconds = 0;
          elapsedHandle = $interval(function() {
            scope.elapsedSeconds = Math.floor((Date.now() - elapsedFrom) / 1000);
          }, 1000);
        }

        function stopElapsed() {
          if (elapsedHandle && $interval && typeof $interval.cancel === 'function')
            $interval.cancel(elapsedHandle);
          elapsedHandle = null;
          scope.elapsedSeconds = null;
        }

        // Minutes only once there are any: "0:47" reads as a stopwatch on a
        // wait that has not become one yet.
        scope.elapsedLabel = function() {
          const total = scope.elapsedSeconds;
          if (total === null || total === undefined) return null;
          if (total < 60) return `${total}s`;

          const minutes = Math.floor(total / 60);
          const seconds = total % 60;
          return `${minutes}m ${seconds < 10 ? '0' : ''}${seconds}s`;
        };

        scope.$on('$destroy', stopElapsed);

        // Past deals waiting to be read, and what a sitting has recorded.
        // Separate from `files` and `importing` because these write to the
        // observation ledger rather than to the playbook, and the two can be in
        // flight over the same screen without meaning the same thing.
        scope.redlineDeals = [];
        scope.importingRedline = false;
        scope.redlineProgress = null;
        scope.redlineSummary = null;
        // How many concepts have enough recorded history to draft a fallback
        // from. Rebuilt with the rail, because that is where the proposals are
        // already joined to the vocabulary.
        scope.draftableCount = 0;
        scope.historyOfferCount = 0;
        scope.historyConflictCount = 0;
        scope.historyGapCount = 0;

        // Set only by taking the third way in. The start plate is otherwise
        // driven by whether the playbook is empty, so importing or drafting
        // dismisses it by making it untrue rather than by setting a flag.
        scope.startDismissed = false;
        // Which optional source is open, if any. Collapsed by default: the
        // preparation is optional and used to be the loudest thing on a screen
        // whose subject is a commitment.
        scope.openSource = null;
        scope.sourcesOpen = false;
        scope.reviewingDecided = false;
        // What the two selects on this screen write to, and an OBJECT for the
        // reason `conceptFilter` is one. Both live inside `ng-if`s, and
        // `ng-model` on a bare name writes to the ng-if's child scope: the
        // parent — which every function in this closure reads — never sees it.
        // The document-type select made that visible, because `load()` sets
        // `loading`, the bar's `ng-if` goes false, and the child holding the
        // choice is destroyed. Its replacement inherits the parent's unchanged
        // value, so picking Amendment put Lease back on screen. The form picker
        // had the same defect and no symptom: `readForm()` would have read
        // whichever form was preselected, under the name of the one showing.
        scope.picker = { documentType: null, formId: null };
        // Reading a form: how far along, and what came back.
        scope.readingForm = false;
        scope.choosingAnotherForm = false;
        scope.formStatus = null;
        scope.formImportId = null;
        scope.formSectionCount = null;
        scope.formPositions = null;

        function errorMessage(error, fallback) {
          return error && error.data && (error.data.error || error.data.errors)
            ? [].concat(error.data.error || error.data.errors).join(', ')
            : fallback;
        }

        function emptyHistory() {
          return {
            n: 0,
            evidenceCount: 0,
            dealsSeen: 0,
            changedIn: 0,
            outcomeDeals: 0,
            recentOutcomeDeals: 0,
            changedInRecent: 0,
            dealCount: 0,
            decisions: {},
            rejectionReasons: [],
          };
        }

        // Only `declaredStance` is read for this. A hard limit is a boundary
        // *within* a concept that is otherwise negotiable — design §8's own
        // example is "the right to relocate at all is not negotiable" beside a
        // fallback that concedes notice days — so a concession that stayed
        // inside the limit is not a contradiction, and matching this pattern
        // against `declaredHardLimit` would fire the report on entries whose
        // declared position was never crossed.
        const hardLinePattern = /\b(?:never (?:be )?(?:conceded|accepted|agreed)|never (?:concede|accept|agree)|(?:non|not)[- ]negotiable|no concessions?(?: allowed)?|(?:will|do|does|must) not (?:concede|accept|agree)|must (?:reject|be rejected))\b/i;
        const rejectionReasonLabels = {
          outside_approved_position: 'Outside approved position',
          legal_or_compliance_risk: 'Legal or compliance risk',
          economics_not_acceptable: 'Economics not acceptable',
          drafting_or_scope_problem: 'Drafting or scope problem',
          already_addressed: 'Already addressed elsewhere',
          deal_specific_reason: 'Deal-specific reason',
          other: 'Other',
          legacy_free_text: 'Earlier free-text reason',
        };

        // The three rungs bind to `<input type="number">`, whose Angular
        // formatter throws `[ngModel:numfmt]` on anything that is not a number
        // and leaves the field blank. A decimal column serialises to "200.0",
        // so a saved ladder came back looking unsaved — the value was in the
        // database the whole time and nothing could render it.
        //
        // The serialiser is fixed at source; this is here so a second source of
        // the same shape — a cached payload, another endpoint, a column someone
        // adds later — cannot blank a customer's numbers again.
        const RUNG_FIELDS = ['stanceValue', 'fallbackValue', 'hardLimitValue'];

        function numericRungs(entry) {
          const rungs = {};
          RUNG_FIELDS.forEach(field => {
            const value = entry[field];
            if (value === null || value === undefined || value === '') return;

            const number = Number(value);
            // Not a number and not blank means something we cannot render and
            // must not invent. Dropped to null, which the editor draws as an
            // empty rung rather than as a field that refuses to paint.
            rungs[field] = Number.isFinite(number) ? number : null;
          });
          return rungs;
        }

        function decorateEntry(entry, index) {
          const observed =
            scope.historyByConcept[entry.concept] ||
            entry.observed ||
            emptyHistory();
          return Object.assign({}, entry, numericRungs(entry), {
            _draftId: entry._draftId || `playbook-entry-${Date.now()}-${index}`,
            observed: observed,
            sourceCount: entry.sourceCount || 1,
          });
        }

        // Everything the merge copies, and therefore everything it has to be
        // able to put back.
        const MERGEABLE_FIELDS = [
          'sourceText',
          'formPosition',
          'declaredStance',
          'declaredFallback',
          'declaredHardLimit',
          // The import fills this now — an approval matrix names an approver in
          // its own column, and that column used to be glued onto the stance.
          // Left out of this list, merging two positions on one concept dropped
          // whichever escalation the losing side carried, silently.
          'escalationTrigger',
          'note',
        ];

        // Discarded entries stay in `scope.entries` and are filtered out here.
        // Splicing them was what made a discard unrecoverable: the only copy of
        // an imported position — including the verbatim `sourceText` — lived in
        // that array, and the obvious way to get one back was to re-import,
        // which replaces the array wholesale and destroys every other entry
        // written since.
        function liveEntries() {
          return scope.entries.filter(entry => !entry._discarded);
        }

        // A latch, deliberately. Typing and then retyping the original value
        // still counts as dirty, which over-warns rather than under-warns — the
        // failure this guards against is silent loss of an afternoon's work.
        function markDirty() {
          scope.dirty = true;
          persistDraft();
        }

        scope.markDirty = markDirty;

        // Out of the deliberate-none state and back into the ladder. The
        // reverse — declaring no position — belongs to the decline action in
        // P2-04; this is only the way back out of a decision already taken.
        scope.takeAPosition = function(entry) {
          entry.state = 'proposed';
          markDirty();
          scope.refreshDuplicates();
        };

        // A named owner who has since been deactivated. Shown rather than
        // dropped: silently erasing a stale path is worse than saying it is
        // stale, and detecting this is why the owner is a user reference.
        scope.staleEscalationOwner = function(entry) {
          if (!entry || !entry.escalationOwnerId) return false;

          const owner = (scope.escalationOwners || []).find(
            candidate => candidate.id === entry.escalationOwnerId,
          );

          return Boolean(owner && owner.active === false);
        };

        // The session, not the positions. `state: proposed` is a saved-but-
        // unconfirmed POSITION; this is an unsaved SESSION, and both are needed
        // — a proposal that survives a browser crash is still not a
        // declaration.
        //
        // Required rather than nice: all three usage patterns are a form
        // manager working over weeks, a CS-attended call of forty-five minutes,
        // or GC onboarding. None of them can be done in one sitting, and the
        // navigation guards make losing the work a decision rather than an
        // accident without making it survive.
        //
        // localStorage, scoped per company and document type. It holds the
        // reviewer's own positions in their own browser — not a log, not a
        // ledger row, not an analytics payload, which is where lp-007 §16
        // forbids customer prose.
        function draftKey() {
          return `lp-playbook-draft:${scope.companyId || 'self'}:${
            scope.documentType
          }`;
        }

        function persistDraft() {
          if (!scope.documentType || !window.localStorage) return;

          try {
            window.localStorage.setItem(
              draftKey(),
              JSON.stringify({
                savedAt: new Date().toISOString(),
                // Carried with the entries because it describes them. Without
                // it a restored import comes back as 31 positions with no
                // record that 2 were dropped and some fields removed — the one
                // reading of the document that says what did NOT survive it.
                importSummary: scope.importSummary || null,
                reviewBatch: scope.reviewBatch || 0,
                reviewSource: scope.reviewSource || null,
                // What this draft was taken against. restoreDraft compares it to
                // the server's playbookUpdatedAt to decide whether anyone saved
                // while the reviewer was away — and it was never written, so the
                // comparison was `undefined !== <timestamp>` and EVERY restored
                // draft was declared stale. The reviewer was asked to choose
                // between their work and a saved playbook that had not moved,
                // and the reassuring-sounding option discarded work that was
                // never in conflict.
                basedOn: scope.playbookUpdatedAt || null,
                entries: scope.entries,
              }),
            );
          } catch (error) {
            // A full or disabled store must not take the editor down with it.
            if (window.isDebug)
              console.warn('[playbook] draft not saved', error);
          }
        }

        // What the choice actually costs, rather than asking for it blind.
        //
        // The same shape the audit log has always used for a change —
        // getDiffText marks what went in with <ins> and what came out with
        // <del>, which is the redline vocabulary a drafter already reads in the
        // document. Per FIELD, because a playbook entry is structured: a line
        // diff of serialised entries would be noise about key order.
        const COMPARED_FIELDS = [
          { key: 'declaredStance', label: 'Preferred position' },
          { key: 'declaredFallback', label: 'Fallback' },
          { key: 'declaredHardLimit', label: 'Minimum acceptable' },
          { key: 'escalationTrigger', label: 'Escalate when' },
          { key: 'note', label: 'Why' },
          { key: 'sourceText', label: 'Imported text' },
        ];

        const DIFF_SANITIZE = {
          allowedTags: ['ins', 'del'],
          allowedAttributes: {},
        };

        function comparableValue(value) {
          return String(value === null || value === undefined ? '' : value).trim();
        }

        // getDiffText interpolates values into markup without escaping, and
        // these are sentences a reviewer typed. Sanitised down to the two marks
        // it is allowed to produce.
        function diffMarkup(before, after) {
          return safeSanitize(getDiffText(before, after), DIFF_SANITIZE);
        }

        function fieldDifferences(saved, mine) {
          return COMPARED_FIELDS.map(function(field) {
            const before = comparableValue(saved && saved[field.key]);
            const after = comparableValue(mine && mine[field.key]);
            if (before === after) return null;

            return {
              label: field.label,
              html: diffMarkup(before, after),
              onlyMine: !before,
              onlySaved: !after,
            };
          }).filter(Boolean);
        }

        function byConcept(entries) {
          const map = {};
          (entries || []).forEach(function(entry) {
            if (entry && entry.concept && !entry._discarded)
              map[entry.concept] = entry;
          });
          return map;
        }

        // Only what the decision turns on. Concepts that match are counted, not
        // listed — a comparison that shows 41 rows to explain 2 differences is
        // the same problem as the banner that showed none.
        scope.buildStaleComparison = function() {
          if (!scope.staleDraft) {
            scope.staleComparison = null;
            return;
          }

          const saved = byConcept(scope.entries);
          const mine = byConcept(scope.staleDraft.entries);
          const conceptIds = Object.keys(saved)
            .concat(Object.keys(mine))
            .filter(function(id, index, all) {
              return all.indexOf(id) === index;
            });

          const rows = [];
          let identical = 0;

          conceptIds.forEach(function(conceptId) {
            const savedEntry = saved[conceptId];
            const myEntry = mine[conceptId];

            if (!savedEntry) {
              rows.push({ conceptId: conceptId, kind: 'only_mine', fields: [] });
              return;
            }
            if (!myEntry) {
              rows.push({ conceptId: conceptId, kind: 'only_saved', fields: [] });
              return;
            }

            const fields = fieldDifferences(savedEntry, myEntry);
            const stateChanged = savedEntry.state !== myEntry.state;

            if (!fields.length && !stateChanged) {
              identical += 1;
              return;
            }

            rows.push({
              conceptId: conceptId,
              kind: 'changed',
              fields: fields,
              savedState: stateChanged ? savedEntry.state : null,
              myState: stateChanged ? myEntry.state : null,
            });
          });

          scope.staleComparison = {
            rows: rows,
            identical: identical,
            open: false,
          };
        };

        scope.toggleStaleComparison = function() {
          if (!scope.staleComparison) scope.buildStaleComparison();
          if (scope.staleComparison)
            scope.staleComparison.open = !scope.staleComparison.open;
        };

        // Explicitly chosen, never assumed.
        scope.applyStaleDraft = function() {
          if (!scope.staleDraft) return;

          scope.entries = scope.staleDraft.entries.map(decorateEntry);
          scope.draftRestoredAt = scope.staleDraft.savedAt;
          scope.staleDraft = null;
          scope.staleComparison = null;
          scope.dirty = true;
          scope.refreshDuplicates();
        };

        scope.discardDraft = function() {
          if (window.localStorage) window.localStorage.removeItem(draftKey());
          scope.draftRestoredAt = null;
          scope.staleDraft = null;
          scope.staleComparison = null;
          scope.playbookUpdatedAt = null;
          scope.conceptFrequency = {};
          scope.proposals = {};
          scope.staleDraft = null;
          load(scope.documentType, false);
        };

        // Restoration is announced, never silent. Silent restoration reads as
        // having lost nothing, which is indistinguishable from having lost
        // everything if the reviewer cannot remember what they typed.
        function restoreDraft() {
          if (!window.localStorage) return false;

          let draft = null;
          try {
            draft = JSON.parse(
              window.localStorage.getItem(draftKey()) || 'null',
            );
          } catch (error) {
            return false;
          }
          if (!draft || !Array.isArray(draft.entries) || !draft.entries.length)
            return false;

          // The saved playbook has moved on since this draft was taken. Auto-
          // applying would hide newer work and then overwrite it on the next
          // save, which is the same silent-overwrite failure as committing an
          // empty playbook.
          const serverMoved =
            scope.playbookUpdatedAt &&
            (draft.basedOn || null) !== scope.playbookUpdatedAt;

          if (serverMoved) {
            scope.staleDraft = draft;
            scope.draftRestoredAt = null;
            scope.buildStaleComparison();
            return false;
          }

          scope.entries = draft.entries.map(decorateEntry);
          // Cleared for the same reason, and this is the one that showed.
          //
          // `load` runs `setEntries` with the SERVER's entries first, which
          // resolves and pins `activeKey`; then the local draft replaces every
          // entry and refreshes again — and `resolveActive` finds that key
          // still present and keeps it. So a reviewer whose saved playbook was
          // all confirmed, with every draft sitting unsaved in the browser,
          // landed on a confirmed concept while dozens of drafts waited. The
          // landing has to be computed against the entries that ended up on
          // screen, not the ones that were there for one pass.
          scope.activeKey = null;
          scope.draftRestoredAt = draft.savedAt;
          scope.importSummary = draft.importSummary || null;
          scope.reviewBatch = draft.reviewBatch || 0;
          scope.reviewSource = draft.reviewSource || null;
          // Set before the refresh below, for the same reason: a restored
          // session's progress is measured against the same import.

          scope.staleDraft = null;
          scope.dirty = true;
          scope.refreshDuplicates();
          return true;
        }

        // Replaces the working set — which means any claim about the PREVIOUS
        // one stops being true here. The import summary outlived its entries
        // through `discardDraft`: the draft went, the server's empty playbook
        // loaded, and the screen showed "Start on the 41 concepts" directly
        // above a banner reading "32 entries across 30 concepts". Cleared at
        // the boundary rather than in `discardDraft`, because every path that
        // swaps the entries has the same problem and only one of them was the
        // one noticed.
        // lp-013 §4. Confirming and importing are events; typing is not.
        //
        // A draft is not policy, so a keystroke changes nothing a drafter will
        // ever be told — logging one would bury the six moments that do. Guarded
        // because the editor runs in tests and in the admin area, where
        // `window.track` is not necessarily present, and an audit call must
        // never be the reason a reviewer cannot confirm a position.
        function audit(EventClass, data) {
          try {
            if (window.track && typeof EventClass === 'function') {
              window.track.event(new EventClass(data));
            }
          } catch (error) {
            // Deliberately swallowed. See above.
          }
        }

        // lp-014. The export and the blank template are one document and one
        // request; `template` is the only thing that differs, and it is chosen
        // from whether anything has been decided rather than from a second
        // control the customer has to understand.
        scope.downloading = false;
        scope.downloadError = null;

        scope.downloadDocument = function() {
          if (scope.downloading) return;

          scope.downloading = true;
          scope.downloadError = null;

          ApiService.downloadPlaybookDocument({
            documentType: scope.documentType,
            template: !scope.decidedCount,
            // Off. It is a written record of what the firm has conceded, in a
            // file that gets emailed, and nobody has asked for it here yet.
            includeHistory: false,
          })
            .then(function(response) {
              const name = filenameFrom(response) || 'Playbook.docx';
              // An export is a disclosure: the firm's negotiating floor leaves
              // the building in a file somebody can forward.
              audit(window.PlaybookExportedEvent, {
                documentType: scope.documentType,
                template: !scope.decidedCount,
                fileName: name,
              });
              ApiService.downloadFile(
                name,
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                response.data,
              );
            })
            .catch(function() {
              scope.downloadError =
                'Could not build the document. Please try again.';
            })
            .finally(function() {
              scope.downloading = false;
            });
        };

        // The server names the file — "Lease Playbook.docx" against "Lease
        // Template.docx" — and it is the only side that knows which one it
        // built, so the name is read back off the response rather than guessed
        // from the flag we sent.
        function filenameFrom(response) {
          const disposition =
            response && response.headers && response.headers('content-disposition');
          const match = /filename[^;=\n]*=(?:UTF-8'')?"?([^";\n]*)"?/i.exec(
            disposition || '',
          );
          return match ? decodeURIComponent(match[1]) : null;
        }

        function setEntries(entries) {
          scope.entries = (entries || []).map(decorateEntry);
          scope.importSummary = null;
          scope.reviewBatch = 0;
          scope.reviewSource = null;
          scope.dirty = false;
          // The working set was replaced, so where the reviewer was is a
          // question about a list that no longer exists. `resolveActive` keeps
          // a key it can still find, which is right for a refresh and wrong
          // here — see `restoreDraft` for what that cost.
          scope.activeKey = null;
          scope.refreshDuplicates();
        }

        function load(documentType, keepDraft) {
          // A refresh over a screen that already has content is not a load.
          //
          // `loading` blanks the workspace and drops a full-height placeholder
          // where the reviewer was working, which is right the first time and
          // wrong every time after — and this runs after an import and again
          // when the redline poll drains, so it landed in the middle of a
          // progress panel and a rail that were mid-sentence.
          const hasScreen = Boolean((scope.rail || []).length);
          scope.loading = !hasScreen;
          scope.refreshing = hasScreen;
          scope.error = null;
          return ApiService.getPlaybook(documentType)
            .then(function(response) {
              const data = response.data || {};
              scope.documentTypes = data.documentTypes || scope.documentTypes;
              scope.documentTypeLabels =
                data.documentTypeLabels || scope.documentTypeLabels || {};
              scope.escalationOwners = data.escalationOwners || [];
              scope.forms = data.forms || [];
              // Preselected. A select bound to null shows an empty row and
              // leaves the Read button disabled with nothing on screen saying
              // why — the reviewer has to guess that the empty select is the
              // problem.
              if (!scope.forms.some(form => form.id === scope.picker.formId))
                scope.picker.formId = scope.forms.length ? scope.forms[0].id : null;
              scope.documentTypeLabel =
                scope.documentTypeLabels[scope.documentType] ||
                scope.documentType;
              scope.vocabulary = data.vocabulary || [];
              scope.historyByConcept = data.observationHistory || {};
              scope.playbookUpdatedAt = data.playbookUpdatedAt || null;
              scope.conceptFrequency = data.conceptFrequency || {};
              scope.proposals = data.proposals || {};
              scope.proposalMinimumDeals = data.proposalMinimumDeals || 2;
              // Concepts this history has something to say about but not yet
              // enough: one deal short of the bar. It is the number that answers
              // "how many more do I need" with something better than "more" —
              // every one of these turns into a fallback on the next negotiation
              // that touches it.
              scope.nearlyDraftableCount = Object.keys(scope.historyByConcept).filter(
                function(concept) {
                  const observed = scope.historyByConcept[concept] || {};
                  return (
                    observed.landingSampleSize > 0 &&
                    observed.outcomeDeals < scope.proposalMinimumDeals
                  );
                },
              ).length;
              if (!keepDraft) {
                // Rows this screen never staged: a sitting left running in a
                // tab that was closed, or one started from another tab.
                // Adopting them is what makes "close the tab, we will email
                // you" true rather than a promise the screen cannot keep.
                //
                // Only on a fresh load. The poll itself calls load(keepDraft)
                // when the queue drains, and resuming from there is mutual
                // recursion — poll, load, resume, poll — which ran the heap out
                // rather than merely looping.
                resumeRedlineImports();
                setEntries(data.entries || []);
                // The server's copy is the baseline; an unsaved session on top
                // of it is what the reviewer was actually doing.
                restoreDraft();
              } else scope.refreshDuplicates();
            })
            .catch(function(error) {
              scope.error = errorMessage(
                error,
                'The playbook could not be loaded.',
              );
            })
            .finally(function() {
              scope.loading = false;
              scope.refreshing = false;
            });
        }

        scope.changeDocumentType = function() {
          // Switching type reloads from the server and replaces every entry, so
          // it discards unsaved work exactly as navigating away would. The
          // select has already changed by the time this fires, so a refusal has
          // to put it back.
          if (scope.dirty) {
            if (
              !window.confirm(
                'You have unsaved playbook changes. Switching document type discards them.',
              )
            ) {
              setDocumentType(scope.lastDocumentType);
              return;
            }
          }

          scope.lastDocumentType = scope.documentType;
          scope.importSummary = null;
          scope.savedSummary = null;
          scope.files = [];
          scope.dropError = null;
          scope.droppingOn = null;
          // Looked up now rather than held from link time: whichever of the two
          // inputs is on screen is the one whose filename needs clearing, and
          // on a type switch it is often neither.
          const fileInput = findFileInput();
          if (fileInput) fileInput.value = '';
          // A different document type is a different playbook, so the choice to
          // write this one by hand does not carry over to it.
          scope.startDismissed = false;
          // Nor does a tally of redlines recorded against the type just left, or
          // a queue of deals staged for it.
          scope.redlineSummary = null;
          scope.redlineDeals = [];
          stopPolling();
          scope.importingRedline = false;
          const redlineInput = root.querySelector('[data-redline-files]');
          if (redlineInput) redlineInput.value = '';
          if (scope.documentType) load(scope.documentType, false);
          else {
            scope.vocabulary = [];
            setEntries([]);
          }
        };

        scope.importDraft = function() {
          if (!scope.documentType || !scope.files.length || scope.importing)
            return;

          // An import REPLACES the working set — `setEntries` overwrites
          // scope.entries and resets `dirty` — so importing over a playbook
          // that has been worked on destroys every position taken, silently.
          // changeDocumentType has guarded that hazard since it shipped; this
          // path had the same consequence and no guard at all.
          //
          // Only asked when there is something to lose: from the start plate the
          // playbook is empty by definition, and a confirm there would be a
          // dialog about discarding nothing.
          if (
            scope.entries.length &&
            !window.confirm(
              'Importing replaces every position in this playbook, including any you have confirmed. Continue?',
            )
          )
            return;

          scope.error = null;
          scope.savedSummary = null;
          scope.importing = true;
          startElapsed();
          // Declining the consent modal rejects with no response, so it is
          // handled here rather than falling through to the request catch —
          // otherwise a drafter who cancelled deliberately is told the import
          // failed.
          AiServicesConsentService.ensureAiServicesConsent().then(
            function() {
              return ApiService.importPlaybook({
                documentType: scope.documentType,
                files: scope.files,
              })
                .then(function(response) {
                  const data = response.data || {};
                  scope.vocabulary = data.vocabulary || scope.vocabulary;
                  // After, not before: setEntries clears the summary, because
                  // it is the boundary where the entries a summary describes
                  // are replaced.
                  setEntries(data.entries || []);
                  scope.importSummary = data.summary;
                  audit(window.PlaybookWrittenPlaybookImportedEvent, {
                    documentType: scope.documentType,
                    // The customer's own filename does not travel. This channel
                    // is forwarded off platform, and a document a firm uploads
                    // is routinely named after the counterparty or the property
                    // — "Ferrara's Kitchen tenant markup.docx". The exclusion
                    // list on `deal_context` already treats filenames as
                    // sensitive; this is the same rule on the way out.
                    // The export event keeps its name because we generate it.
                    fileCount: (scope.files || []).length,
                    entryCount: (data.summary && data.summary.entryCount) || 0,
                    conceptCount: (data.summary && data.summary.conceptCount) || 0,
                  });
                  beginReview('your written playbook');
                  // After the assignment, because the progress the panel shows
                  // is measured against what the import returned and
                  // `setEntries` has just cleared the summary on its way past.
                  scope.refreshDuplicates();
                  // An import is unsaved work, and nothing else was saying so.
                  // `setEntries` clears `dirty` because its other caller is the
                  // server load, so the import has to mark itself — and
                  // markDirty is what writes the localStorage draft.
                  //
                  // Reading a form never needed this and that is what hid it:
                  // that result is a server-side row the screen re-fetches on
                  // every load, so it survives a reload for free. A written
                  // playbook is extracted once, inside this one request, and
                  // held nowhere but here. A reload threw away the whole
                  // extraction — minutes of reading, and the only copy.
                  //
                  // After `load`, not before: persistDraft stamps `basedOn`
                  // from `playbookUpdatedAt`, and load is what refreshes it.
                  // Stamped from a stale value, restoreDraft reads the draft as
                  // taken against an older playbook and offers to discard it.
                  return load(scope.documentType, true).then(function() {
                    // Onto the imported work, explicitly. resolveActive only
                    // reaches for the first thing needing attention when the
                    // current selection has GONE — and by the time an import
                    // lands, the screen has been open long enough to have
                    // selected the first concept in the rail, which the import
                    // may well not have touched. 35 positions arrived and the
                    // pane still read "No position is recorded for this
                    // concept, and nobody has looked at it yet."
                    scope.reviewNext();
                    markDirty();
                  });
                })
                .catch(function(error) {
                  scope.error = errorMessage(
                    error,
                    'The playbook could not be imported.',
                  );
                })
                .finally(function() {
                  scope.importing = false;
                  stopElapsed();
                });
            },
            function() {
              scope.importing = false;
              stopElapsed();
              scope.error =
                'AI Services consent is required to import a playbook.';
            },
          );
        };

        // Arrays on scope, not functions in the template.
        //
        // These were bound as calls inside `ng-repeat` and `ng-if`, so Angular
        // re-ran all three on every digest — and `disagreements` runs a regex
        // per classified entry, which meant one keystroke in a textarea
        // re-filtered the whole playbook. Survivable on a screen visited once;
        // untenable at 41 buckets rendered in two panes, which is what the rail
        // needs.
        //
        // Rebuilt from `refreshLists`, which runs where the data actually
        // changes: a concept assignment, a discard, a restore, a merge, an
        // import, a load.
        scope.classified = [];
        scope.unclassified = [];
        scope.disagreementList = [];
        scope.rail = [];
        scope.unclassifiedRail = [];
        scope.activeKey = null;
        scope.activeItem = null;
        scope.decidedCount = 0;
        scope.enrichedCount = 0;
        scope.draftRestoredAt = null;
        scope.conceptCount = 0;

        function buildClassified() {
          return scope.entries.filter(entry => !!entry.concept);
        }

        function buildUnclassified() {
          return scope.entries.filter(entry => !entry.concept);
        }

        scope.conceptLabel = function(conceptId) {
          const concept = scope.vocabulary.find(item => item.id === conceptId);
          return concept ? concept.label : conceptId;
        };

        // The words the rail uses, so a status change in the comparison reads
        // the same as the status everywhere else on this screen.
        scope.stateLabel = function(state) {
          if (state === 'declared') return 'Confirmed';
          if (state === 'no_position') return 'No position';
          if (state === 'proposed') return 'Draft';
          return state || 'Not started';
        };

        // Where this position came from, and how much stands behind it, as one
        // sentence. They were two — "Observed history" beside "n = 0
        // observations" — which put statistical notation in a header for an
        // audience that has no reason to read it, and stacked four things on one
        // line: title, origin, state, support.
        //
        // A count only when there is one. "n = 0 observations" claimed evidence
        // in the act of reporting none.
        scope.provenanceLabel = function(entry) {
          if (!entry) return '';

          const observed = (entry.observed || emptyHistory()).evidenceCount;
          const sources = entry.sourceCount || 1;
          const decisions = count =>
            `${count} past ${count === 1 ? 'decision' : 'decisions'}`;

          if (entry.source === 'form') {
            return 'Read from your form';
          }

          if (entry.source === 'observed_history') {
            return observed
              ? `Drafted from ${decisions(observed)}`
              : 'Drafted from past decisions';
          }

          if (entry.source === 'both') {
            return observed
              ? `From your written playbook and ${decisions(observed)}`
              : 'From your written playbook and past decisions';
          }

          if (entry.source === 'merged') {
            return `Merged from ${sources} ${
              sources === 1 ? 'position' : 'positions'
            } in your written playbook`;
          }

          if (entry.source === 'written_playbook') {
            return sources > 1
              ? `From ${sources} places in your written playbook`
              : 'From your written playbook';
          }

          return 'Written here';
        };

        scope.dismissDraftNotice = function() {
          scope.draftNoticeDismissed = true;
        };

        // Whether the progress panel is up. The second-source lines live inside
        // it when it is, and stand alone when it is not — one question deserves
        // one place to answer it, and this is the condition the panel itself
        // uses.
        scope.summaryVisible = function() {
          return Boolean(
            scope.reviewBatch && (scope.classifiedCount || scope.unclassifiedCount),
          );
        };

        scope.observedLabel = function(entry) {
          const observed = entry.observed || emptyHistory();
          if (!observed.evidenceCount)
            return 'No past decisions recorded yet';
          const changedIn =
            typeof observed.changedIn === 'number'
              ? observed.changedIn
              : observed.dealCount || 0;
          const dealsSeen = observed.dealsSeen || changedIn;
          const seen = observed.n || dealsSeen;
          const outcomeDeals = observed.outcomeDeals || 0;
          const seenLabel = `Seen in ${seen} ${seen === 1 ? 'deal' : 'deals'}`;
          if (!outcomeDeals) return seenLabel;

          // "conceded in 2 of the 2 with a known outcome" is a true sentence
          // nobody says out loud. Where every outcome we know went the
          // counterparty's way, say that; where some did not, say which, and
          // say what the denominator IS rather than naming it.
          if (changedIn === outcomeDeals)
            return `${seenLabel}. Conceded in ${
              outcomeDeals === 1 ? 'the one we know the outcome of' : 'every one'
            }.`;

          return `${seenLabel}. Conceded in ${changedIn} of the ${outcomeDeals} we know the outcome of.`;
        };

        // What past deals actually settled on, worst notation first.
        //
        // `(n = 5)` is statistician's shorthand on a screen read by lawyers, and
        // it sat beside a heading counting DEALS while itself counting accepted
        // changes — two denominators, no explanation, looking like a
        // contradiction.
        //
        // The other two lines were worse than unclear. `shortestLanding` and
        // `longestLanding` are `min_by`/`max_by` over STRING LENGTH
        // (playbook_entry.rb), so "Shortest accepted landing" tells a reader we
        // measured the smallest concession. We measured the fewest characters.
        // They are other wordings the concession took, and that is all this can
        // honestly call them — and only when they differ from the one above.
        // NOT on scope, and not called from the template.
        //
        // `ng-repeat` over a function that returns fresh objects is an infinite
        // digest: `$watchCollection` compares the items by reference, every
        // digest hands it new ones, and Angular gives up after ten passes with
        // the DOM half-updated — which is what put two concept panes on screen
        // at once and a loading placeholder beside live content. `track by` does
        // not help; it governs DOM reuse, not the watch.
        //
        // Line 723 of this file already says it: arrays on scope, not functions
        // in the template.
        function observedLandingsFor(entry) {
          const observed = (entry && entry.observed) || emptyHistory();
          const extremes = observed.extremes || {};
          const typical = observed.typicalLanding;
          if (!typical) return [];

          const sample = Number(observed.landingSampleSize) || 0;
          const rows = [
            {
              label: sample
                ? `Most common concession, from ${sample} recorded ${
                    sample === 1 ? 'change' : 'changes'
                  }`
                : 'Most common concession',
              text: typical,
            },
          ];

          const seen = [normalizedLanding(typical)];
          [extremes.shortestLanding, extremes.longestLanding].forEach(text => {
            if (!text) return;
            if (seen.indexOf(normalizedLanding(text)) !== -1) return;

            seen.push(normalizedLanding(text));
            rows.push({ label: 'Also recorded', text: text });
          });

          return rows;
        }

        function normalizedLanding(text) {
          return String(text || '')
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();
        }

        function declaresNeverConceded(entry) {
          return hardLinePattern.test(entry.declaredStance || '');
        }

        // Confirmed positions only. The report says "YOU record this as never
        // conceded; it was conceded in 3 of the last 9", and its entire value is
        // the word you. Run it against a proposed stance and it compares a
        // machine's guess to the firm's history and presents the mismatch as a
        // finding about their policy — the §7 failure in a more persuasive
        // costume, because the report looks analytical.
        function buildDisagreements() {
          return scope.classified.filter(function(entry) {
            const observed = entry.observed || emptyHistory();
            return (
              !entry._discarded &&
              entry.state === 'declared' &&
              declaresNeverConceded(entry) &&
              observed.changedInRecent > 0
            );
          });
        }

        // The one place the three lists are rebuilt. `refreshDuplicates` already
        // ran on every structural change, so this rides along with it rather
        // than adding a second thing callers have to remember.
        // The rail lists every concept in the vocabulary, not only the ones with
        // an entry. That is the whole point of the denominator: "18 of 41
        // decided" is only true if the 23 nobody has touched are on the list.
        //
        // An untouched concept has no row in the database and does not get one
        // by being looked at — selecting it shows a prompt, and the entry is
        // written when the reviewer actually decides something. Materialising on
        // click would fill the playbook with empty rows for every concept
        // somebody browsed past.
        // An offer is a quote, a drafted rung, or both. A concept whose form
        // clause offers no choice comes back quoted with an empty ladder; one
        // the form is silent on comes back with nothing and is not offered at
        // all, which is §8's distinction and the whole point of allowing the
        // reader to answer nothing.
        function hasOffer(position) {
          return Boolean(
            position &&
              (position.formPosition ||
                rungText(position.preferredPosition) ||
                rungText(position.fallback) ||
                rungText(position.minimumAcceptable)),
          );
        }

        function formPositionFor(conceptId) {
          if (!scope.formPositions) return null;
          const match = scope.formPositions.find(
            position => position.concept === conceptId && hasOffer(position),
          );
          return match || null;
        }

        function buildRail() {
          const byConcept = {};
          // eslint-disable-next-line prefer-const
          scope.entries.forEach(function(entry) {
            if (entry.concept && !entry._discarded)
              byConcept[entry.concept] = entry;
          });

          const items = (scope.vocabulary || []).map(function(concept) {
            const entry = byConcept[concept.id];
            const formPosition = entry ? null : formPositionFor(concept.id);
            return {
              key: `concept:${concept.id}`,
              conceptId: concept.id,
              label: concept.label,
              entry: entry || null,
              duplicate: Boolean(entry && entry._duplicate),
              state: entry ? entry.state : 'untouched',
              observations: (scope.conceptFrequency || {})[concept.id] || 0,
              proposal: entry
                ? null
                : (scope.proposals || {})[concept.id] || null,
              // What the form says about this concept, when a form has been
              // read. Offered exactly like a history proposal: named, quoted,
              // and never applied on its own.
              formPosition: formPosition,
              // Built here so the pane can repeat over an array that only
              // changes when the rail does.
              formRungs: formOfferRungsFor(formPosition),
            };
          });

          // Most-negotiated first, then alphabetical. A concept nobody has ever
          // touched needs no position, and asking for one spends the reviewer's
          // attention on the least valuable rows in the table.
          //
          // The count rides along on each item so the order has a visible
          // reason — a reordered list with no explanation reads as arbitrary,
          // which is worse than alphabetical.
          return items.sort(function(left, right) {
            if (left.observations !== right.observations)
              return right.observations - left.observations;

            return String(left.label).localeCompare(String(right.label));
          });
        }

        // Unclassified positions have no concept, so they cannot occupy a
        // concept bucket. Pinned above the vocabulary rather than buried in it:
        // they are the only thing that blocks a save, and hiding them under 41
        // concepts they do not belong to would bury the work that matters most.
        function buildUnclassifiedRail() {
          return scope.entries
            .filter(entry => !entry.concept && !entry._discarded)
            .map(function(entry) {
              const text = String(entry.sourceText || '').trim();
              return {
                key: `draft:${entry._draftId}`,
                entry: entry,
                label: text.length > 64 ? `${text.slice(0, 64)}…` : text,
                state: 'unclassified',
              };
            });
        }

        function refreshLists() {
          scope.classified = buildClassified();
          scope.unclassified = buildUnclassified();
          scope.disagreementList = buildDisagreements();
          scope.rail = buildRail();
          scope.unclassifiedRail = buildUnclassifiedRail();
          scope.decidedCount = scope.rail.filter(isDecided).length;
          // Concepts nobody has answered yet. "All 32 imported positions have
          // been reviewed" is not the same sentence as "the playbook is done",
          // and the panel had no way to tell the reviewer which one was true.
          scope.untouchedCount = scope.rail.filter(
            item => item.state === 'untouched',
          ).length;
          scope.conceptCount = scope.rail.length;
          // Whether the rail is in any order worth explaining. With no history
          // recorded the sort is alphabetical and saying "most negotiated first"
          // would be describing something that has not happened.
          scope.anyObservations = scope.rail.some(item => item.observations > 0);
          // The number the start plate is allowed to say. It counts concepts
          // that have BOTH no entry yet and a proposal the server built from
          // observed history — never the vocabulary, and never the ledger's raw
          // row count, because neither is the number of positions this button
          // would actually draft.
          scope.draftableCount = scope.rail.filter(
            item => !item.entry && item.proposal,
          ).length;

          // The two groups the progress line already implies. `decidedCount` has
          // always been declared + no_position; the list said four states in two
          // competing channels while the number above it said two. Grouping does
          // not add structure, it removes a contradiction.
          //
          // Order within each group is buildRail's — most-negotiated first, then
          // alphabetical. Re-sorting inside a group would break the only way a
          // reviewer finds a concept they half-remember.
          scope.railToDecide = scope.rail.filter(item => !isDecided(item));
          scope.railDecided = scope.rail.filter(isDecided);

          // Recomputed here as well as on decision, so reopening an entry or
          // discarding an import takes the done state back down. A screen that
          // says the queue is clear while a row waits in it is worse than one
          // that never said so.
          scope.queueCleared =
            scope.rail.length > 0 &&
            scope.railToDecide.length === 0 &&
            (scope.unclassifiedRail || []).length === 0;

          // Reset while there is still a queue, so that finishing it a second
          // time — after reopening a row and deciding it again — arrives at the
          // ending rather than staying on the row that was just closed.
          if (!scope.queueCleared) scope.reviewingDecided = false;

          // Depth, not progress. A position with a fallback or a limit or an
          // escalation path is better than one without; a position with none of
          // them is still a working position, because the preferred position
          // alone is what turns the card on.
          //
          // A COUNT, never a fraction. 41 concepts times 4 fields is 164 cells
          // and most of them will never be filled — no source reaches the
          // escalation column and most concepts will never have a meaningful
          // fallback — so a denominator here would invent an incompleteness
          // that does not exist and nag about it forever.
          scope.enrichedCount = liveEntries().filter(
            entry =>
              entry.state === 'declared' &&
              [
                entry.declaredFallback,
                entry.declaredHardLimit,
                entry.escalationTrigger,
              ].some(value => String(value || '').trim()),
          ).length;
          resolveActive();
        }

        // Whatever the rail is showing on the right. Kept in sync with the rail
        // rather than held independently, so a discard or a restore cannot leave
        // the detail pane rendering a row that is no longer there.
        // What the screen opens on when nothing is selected — first arrival, or
        // the moment the selected row stops existing.
        //
        // It was `all[0]`, the first row of a rail sorted by how often the
        // company negotiates each concept and then alphabetically. On a company
        // with no observation ledger that is pure alphabetical, so importing 32
        // positions landed the reviewer on "Additional Rent" — a concept holding
        // none of them — under the heading "Not yet reviewed" and the sentence
        // "nobody has looked at it yet". The screen's answer to "what now" was a
        // concept with nothing in it, and the 32 drafts were somewhere down the
        // rail.
        //
        // Unclassified first because they are the only thing that blocks a save;
        // then drafts, which are the ones with text waiting to be read; then
        // untouched, which have nothing to read and still need a decision.
        //
        // `untouched` was missing, and the gap only opened once a reviewer had
        // confirmed every draft they imported. With nothing `proposed` left the
        // search fell through to `all[0]` — the most-negotiated concept in the
        // vocabulary, decided or not — so opening a playbook mid-way through
        // landed on something already settled while dozens of concepts still had
        // no decision at all. The job is turning every concept into a decision;
        // a concept nobody has looked at is as undecided as a draft.
        const ATTENTION_ORDER = ['unclassified', 'proposed', 'untouched'];

        function firstNeedingAttention(all) {
          for (const state of ATTENTION_ORDER) {
            const found = all.find(item => item.state === state);
            if (found) return found;
          }

          // Belt and braces: the list above is every undecided state, so this
          // only runs once the queue really is clear. A decided concept is the
          // right landing then and only then.
          return all.find(item => !isDecided(item)) || all[0] || null;
        }

        // Put the reviewer at the top of the position they were just moved to.
        //
        // A position card runs to about a viewport — the form's language, the
        // stance, the fallback, the limit, the reason, the escalation — and the
        // decision buttons are at the BOTTOM of it. So a reviewer confirms from
        // the bottom of the screen, the next position loads into the same pane,
        // and they are left looking at the foot of a card whose first line they
        // have never seen. Forty-one times.
        //
        // `.content` is the scroll container in this layout, not the window —
        // `.admin-layout` is fixed at calc(100vh - 48px) — so scrollIntoView is
        // used rather than window.scrollTo: it walks up to whatever actually
        // scrolls and does not need to know which element that is.
        //
        // Instant, not smooth. `j` held down through a queue would otherwise
        // spend the whole time animating, and this runs on every step.
        function showActiveFromTheTop() {
          // Guarded the way `startFormPolling` guards `$interval`: the unit
          // harness links this directive against a plain object and injects
          // only the services a given spec cares about, so a scroll is not
          // allowed to be the thing that decides whether the queue works.
          if (typeof $timeout !== 'function') return;

          $timeout(function() {
            const pane = root.querySelector('.playbook-editor__detail');
            if (pane && pane.scrollIntoView)
              pane.scrollIntoView({ block: 'start', behavior: 'auto' });
          });
        }

        function resolveActive() {
          const all = (scope.unclassifiedRail || []).concat(scope.rail || []);
          scope.activeItem =
            all.find(item => item.key === scope.activeKey) ||
            firstNeedingAttention(all);
          scope.activeKey = scope.activeItem ? scope.activeItem.key : null;
          // The detail pane's `entry`, resolved here rather than by `ng-init`
          // on the pane itself. `ng-init` runs once per ELEMENT, and switching
          // between two concepts that both have an entry leaves the pane's
          // `ng-if` true — so the element was never rebuilt, `ng-init` never
          // re-ran, and `entry` stayed pointing at the concept before it. The
          // visible half of that was a rail that would not switch; the other
          // half was every `ng-model` in the pane writing the reviewer's
          // preferred position onto the previous concept's entry.
          scope.entry = scope.activeItem ? scope.activeItem.entry : null;
          resetConceptPicker();
        }

        // 41 options in a bare select, with no way to narrow them. Precomputed
        // rather than an `| filter:` binding for the same reason the queue lists
        // are: a filter expression in a template re-runs on every digest, and
        // this one runs while the reviewer is typing into it.
        // An OBJECT, not a primitive. `ng-if` creates a child scope, so
        // `ng-model="conceptSearch"` wrote to the child and shadowed the
        // parent — the filter read an empty string forever and matched
        // everything. A dotted model resolves through the prototype chain to
        // the property the parent owns.
        // Open on arrival. Collapsing it was meant to keep the rail down to the
        // concepts still owed a decision, but a decided concept is the one thing
        // a returning reviewer comes back to change, and hiding every one of
        // them behind a disclosure put a click between them and their own work.
        // At 41 of 41 it hid the entire rail.
        scope.decidedOpen = true;

        scope.toggleDecided = function() {
          scope.decidedOpen = !scope.decidedOpen;
        };

        // Opened by its own disclosure and nothing else. This used to read
        // `decidedOpen || conceptFilter.text`, on the assumption that the rail
        // had a filter whose matches a collapsed group must not swallow. It has
        // no filter — `conceptFilter` belongs to the concept picker inside an
        // unclassified position, which is a different control on a different
        // part of the screen — so typing a concept name there silently expanded
        // a group in the rail.
        scope.decidedVisible = function() {
          return scope.decidedOpen;
        };

        // One control, not two. A text field to narrow a list and a separate
        // select to choose from it is two things to operate for one decision,
        // and nothing on screen says they are connected — the field reads as a
        // search box that ought to do something on its own.
        //
        // Modelled on the condition field picker in the reports wizard
        // (app/views/reports/_filters_conditions.html.haml), with the keyboard
        // support that pattern does not have. A list of buttons under an input
        // is not a combobox until it is wired as one, and this screen's audience
        // drafts with their hands on the keys.
        scope.conceptFilter = { text: '' };
        scope.filteredVocabulary = [];
        scope.conceptPicker = { open: false, active: -1 };

        // Selection wins over the query. The input shows what was chosen once
        // something is, so the control reads as answered rather than blank.
        function resetConceptPicker() {
          const entry = scope.activeItem && scope.activeItem.entry;
          scope.conceptFilter.text =
            entry && entry.concept ? scope.conceptLabel(entry.concept) : '';
          scope.conceptPicker.open = false;
          scope.conceptPicker.active = -1;
          scope.filterConcepts();
        }

        scope.filterConcepts = function() {
          const needle = String(scope.conceptFilter.text || '')
            .trim()
            .toLowerCase();
          const selected =
            scope.activeItem &&
            scope.activeItem.entry &&
            scope.activeItem.entry.concept;

          scope.filteredVocabulary = (scope.vocabulary || []).filter(function(
            concept,
          ) {
            // The chosen concept survives its own filter. Narrowing the list out
            // from under a selection makes the field look empty and the answer
            // look lost.
            if (concept.id === selected) return true;
            if (!needle) return true;

            return String(concept.label || '')
              .toLowerCase()
              .includes(needle);
          });

          // The highlight cannot point past the end of a list that just shrank.
          if (scope.conceptPicker.active >= scope.filteredVocabulary.length)
            scope.conceptPicker.active = scope.filteredVocabulary.length - 1;
        };

        scope.openConceptPicker = function() {
          scope.conceptPicker.open = true;
          scope.filterConcepts();
        };

        // Closing on blur has to survive the click that caused it: pointer
        // selection uses mousedown, which fires first, so by the time blur runs
        // the choice is already made.
        scope.closeConceptPicker = function() {
          scope.conceptPicker.open = false;
          scope.conceptPicker.active = -1;
        };

        scope.conceptOptionId = function(index) {
          return `playbook-concept-option-${index}`;
        };

        scope.selectConcept = function(entry, concept) {
          if (!entry || !concept) return;

          entry.concept = concept.id;
          scope.conceptPicker.open = false;
          scope.conceptPicker.active = -1;
          scope.markDirty();
          scope.refreshDuplicates();

          // Follow the position to where it now lives. Assigning a concept moves
          // it out of the unclassified queue and into the vocabulary, so its rail
          // key changes from `draft:` to `concept:` — and refreshLists calls
          // resolveActive, which cannot find the old key and falls back to the
          // FIRST item in the rail. The reviewer classified one position and
          // landed on an unrelated concept.
          //
          // Set after the refresh, because the rail it has to resolve against is
          // built by it. `materialise` does the same thing for the same reason.
          scope.activeKey = `concept:${concept.id}`;
          resolveActive();
        };

        scope.onConceptKeydown = function(event, entry) {
          const options = scope.filteredVocabulary || [];
          const key = event.key;

          if (key === 'ArrowDown' || key === 'ArrowUp') {
            event.preventDefault();
            if (!scope.conceptPicker.open) {
              scope.openConceptPicker();
              scope.conceptPicker.active = 0;
              return;
            }
            const step = key === 'ArrowDown' ? 1 : -1;
            const next = scope.conceptPicker.active + step;
            // Stops at both ends rather than wrapping: with 41 concepts,
            // wrapping past the last one reads as the list having jumped.
            scope.conceptPicker.active = Math.max(
              0,
              Math.min(next, options.length - 1),
            );
            return;
          }

          if (key === 'Enter') {
            if (!scope.conceptPicker.open || scope.conceptPicker.active < 0)
              return;
            event.preventDefault();
            scope.selectConcept(entry, options[scope.conceptPicker.active]);
            return;
          }

          // Abandons the typing, not the answer: the input goes back to the
          // concept that is actually assigned.
          if (key === 'Escape') {
            event.preventDefault();
            resetConceptPicker();
            return;
          }

          if (key === 'Tab') scope.closeConceptPicker();
        };

        // Names the position this picker belongs to. Every one of these had the
        // identical accessible name "Assign a concept", so the accessibility
        // tree held N indistinguishable combo boxes.
        scope.positionLabel = function(entry) {
          const text = String((entry && entry.sourceText) || '').trim();
          if (!text) return 'this position';

          return text.length > 60 ? `${text.slice(0, 60)}…` : text;
        };

        // The ending holds the pane until the reviewer asks for a row.
        //
        // `queueCleared` used to gate all four panes directly, so once every
        // concept was decided a decided row could be selected in the rail and
        // the pane never changed — forty-one decisions and no way back into any
        // of them. Asking for a row is what yields the pane; nothing else does,
        // so finishing still lands on the ending it earned.
        scope.showCleared = function() {
          return Boolean(scope.queueCleared && !scope.reviewingDecided);
        };

        scope.returnToSummary = function() {
          scope.reviewingDecided = false;
        };

        scope.selectRailItem = function(item) {
          scope.activeKey = item.key;
          scope.reviewingDecided = true;
          resolveActive();
        };

        // The three exits, equally reachable. Declining is the NORMAL outcome —
        // most landlords never negotiate most concepts — so it sits beside
        // confirming rather than below it. A decline that feels like giving up
        // does not get used, and then the queue never ends.
        const DECIDED_STATES = ['declared', 'no_position'];

        function isDecided(item) {
          return DECIDED_STATES.includes(item.state);
        }

        function setState(entry, state) {
          if (!entry || entry.state === state) return;

          entry.state = state;
          markDirty();
          scope.refreshDuplicates();
        }

        // Deciding moves the row into the other group, so selection has to move
        // with it. Without this the reviewer confirms an entry and the list
        // reflows under the row they are still looking at — which is worse than
        // the ungrouped list this replaced.
        //
        // To the next OUTSTANDING concept, not the next row: the queue is what
        // is left, and stepping onto something already decided is a detour the
        // reviewer then has to step back out of.
        function advancePastDecision(previousKey) {
          const outstanding = (scope.railToDecide || []).concat(
            scope.unclassifiedRail || [],
          );
          const next = outstanding.find(item => item.key !== previousKey);

          // Nothing left. Selection stays where it is and the detail pane shows
          // the queue-clear state — 41 decisions have earned an ending.
          if (!next) {
            scope.queueCleared = true;
            return;
          }

          scope.queueCleared = false;
          scope.activeKey = next.key;
          resolveActive();
          showActiveFromTheTop();
        }

        scope.confirmPosition = function(entry) {
          // A duplicate cannot be confirmed, because it cannot be SAVED: the
          // commit refuses two entries on one concept, and a playbook holds one
          // position per concept by design — lp-007 §8 settled that splitting a
          // company's position on a concept guarantees drift.
          //
          // Without this the editor let a reviewer confirm both halves of a
          // duplicate pair, count them as decided, and walk all the way to
          // "Every concept is decided" — on a playbook the server would refuse.
          // A state that cannot be saved must not be reachable, and must never
          // look like completion.
          if (!entry || entry._duplicate) return;

          const from = scope.activeKey;
          setState(entry, 'declared');
          audit(window.PlaybookPositionConfirmedEvent, {
            concept: entry.concept,
            conceptLabel: scope.conceptLabel(entry.concept),
            documentType: scope.documentType,
          });
          advancePastDecision(from);
        };

        scope.declinePosition = function(entry) {
          // Blocked for the same reason confirming is, and it is worth being
          // explicit about why declining does not help: `refreshDuplicates`
          // counts every live entry on a concept whatever its state, so a
          // declined duplicate is still a duplicate and still refuses the save.
          // It also asserts something incoherent — "no position on holdover"
          // sitting beside a holdover position.
          //
          // Discarding is the verb that resolves it, and the alert above offers
          // it. Neither decision does.
          if (!entry || entry._duplicate) return;

          const from = scope.activeKey;
          setState(entry, 'no_position');
          audit(window.PlaybookPositionDeclinedEvent, {
            concept: entry.concept,
            conceptLabel: scope.conceptLabel(entry.concept),
            documentType: scope.documentType,
          });
          advancePastDecision(from);
        };

        // Back to a draft. Confirming is not irreversible — it is a statement
        // about who is accountable, and a reviewer who confirmed the wrong
        // thing needs a way back that is not "discard and start again".
        scope.reopenPosition = function(entry) {
          setState(entry, 'proposed');
        };

        // Traversal order, which is the reading order down the rail: the
        // unclassified queue, then what is outstanding, then what is decided.
        // The groups are visual — j and k cross them, and a collapsed Decided
        // group does not put its rows out of reach of the keyboard.
        function railItems() {
          return (scope.unclassifiedRail || [])
            .concat(scope.railToDecide || [])
            .concat(scope.railDecided || []);
        }

        scope.moveSelection = function(step) {
          const items = railItems();
          if (!items.length) return;

          const index = items.findIndex(item => item.key === scope.activeKey);
          const next = Math.min(
            Math.max((index === -1 ? 0 : index) + step, 0),
            items.length - 1,
          );
          scope.activeKey = items[next].key;
          resolveActive();
          showActiveFromTheTop();
        };

        // For an audience PRODUCT.md describes as drafting with their hands on
        // the keys, a 41-item queue that needs the mouse 41 times is the whole
        // objection to this feature. The lp-007 critique scored flexibility and
        // efficiency at 1 across these surfaces and this is where that stops
        // being survivable.
        //
        // Ignored while typing, or every stance containing the letter c would
        // confirm the entry being written.
        function isTyping(event) {
          const tag = (event.target && event.target.tagName) || '';
          return ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag.toUpperCase());
        }

        function handleShortcut(event) {
          if (isTyping(event) || event.metaKey || event.ctrlKey || event.altKey)
            return;

          const entry = scope.activeItem && scope.activeItem.entry;
          const key = event.key.toLowerCase();

          if (key === 'j') return scope.$apply(() => scope.moveSelection(1));
          if (key === 'k') return scope.$apply(() => scope.moveSelection(-1));

          // Declining works on a concept that has no row yet, which is most of
          // the queue: most landlords never negotiate most concepts, so a
          // decline is the ordinary outcome and an untouched concept is the
          // ordinary case. `n` did nothing on any of them — the shortcut worked
          // only where the slow path was already short.
          if (!entry) {
            if (key === 'n' && scope.activeItem && !scope.activeItem.entry)
              return scope.$apply(() => scope.declineConcept(scope.activeItem));
            return;
          }
          if (entry._discarded) return;
          if (key === 'c')
            return scope.$apply(() => scope.confirmPosition(entry));
          if (key === 'n')
            return scope.$apply(() => scope.declinePosition(entry));
        }

        document.addEventListener('keydown', handleShortcut);

        // Materialise on decision, not on click. Both paths write the row; they
        // differ only in what the reviewer just said.
        function materialise(conceptId, state) {
          const entry = decorateEntry(
            {
              concept: conceptId,
              state: state,
              source: 'manual',
              sourceCount: 1,
            },
            scope.entries.length,
          );
          scope.entries.push(entry);
          markDirty();
          scope.refreshDuplicates();
          scope.activeKey = `concept:${conceptId}`;
          resolveActive();
          return entry;
        }

        scope.startPosition = function(item) {
          if (!item || item.entry) return;
          materialise(item.conceptId, 'proposed');
        };

        // The drafted fallback, taken only when asked for. The preferred
        // position stays empty on purpose: history records what was CONCEDED,
        // never what was wanted, so drafting the top rung from concessions
        // would assert the opposite of what the evidence says.
        scope.draftFromHistory = function(item) {
          if (!item || item.entry || !item.proposal) return;

          const entry = materialise(item.conceptId, 'proposed');
          entry.declaredFallback = item.proposal.declared_fallback;
          entry.proposalEvidence = item.proposal.proposal_evidence;
          entry.source = 'observed_history';
          scope.refreshDuplicates();
        };

        // The floor for contradicting a customer about their own behaviour.
        //
        // Higher than the two `PROPOSAL_MINIMUM_DEALS` uses, and deliberately.
        // That number gates a SUGGESTION built from evidence; this one gates
        // telling somebody that what they declared is not what they do, and a
        // single deal must not be able to say it.
        const COST_TENSION_MINIMUM_DEALS = 3;

        // lp-012 §3. The three values the server will store, held here as the
        // guard rather than as a render list — the buttons are written out in
        // the template, because an `ng-repeat` over an array the directive has
        // not assigned yet renders nothing at all and does it silently.
        //
        // Still worth keeping as one list: `normalized_concession_cost` drops
        // anything outside it, so a value this file allowed through and the
        // server did not would be a click that saves and comes back empty.
        scope.concessionCosts = ['routine', 'negotiated', 'last_resort'];

        // Absent is the default and has to stay reachable — a reviewer who
        // clicks the wrong one needs a way back to saying nothing, so clicking
        // the selected value clears it.
        scope.setConcessionCost = function(entry, cost) {
          if (!entry || entry._duplicate) return;
          if (scope.concessionCosts.indexOf(cost) === -1) return;

          entry.concessionCost = entry.concessionCost === cost ? null : cost;
          markDirty();
        };

        // Every field that lives under the enrichment disclosure. Named once
        // so `enrichmentOpen` and `enrichmentFilled` cannot drift apart, and so
        // adding a field to that block is one edit rather than three.
        const ENRICHMENT_FIELDS = [
          'declaredFallback',
          'declaredHardLimit',
          'concessionCost',
          'tradeFor',
          'note',
          'ladderMetric',
          'stanceValue',
          'fallbackValue',
          'hardLimitValue',
          'escalationTrigger',
          'escalationOwnerId',
        ];

        // How much is already under there. Shown on the closed summary, because
        // a reviewer who never opens it should be able to tell at a glance that
        // they are not hiding work — theirs or an import's.
        scope.enrichmentFilled = function(entry) {
          if (!entry) return 0;

          return ENRICHMENT_FIELDS.filter(field =>
            String(entry[field] === 0 ? '0' : entry[field] || '').trim(),
          ).length;
        };

        // Collapsed is the default and must not hide work.
        //
        // Open on load wherever a field inside already holds something, or a
        // disagreement between the playbook and past deals is waiting. The
        // disagreement especially: it is the one thing under there the reviewer
        // did not put there themselves, and a collapsed card would be the screen
        // deciding not to mention it.
        scope.enrichmentOpen = function(entry) {
          if (!entry) return false;

          return Boolean(scope.enrichmentFilled(entry) || entry._historyOffer);
        };

        // Open when there is something in it, so an entry that already carries
        // a ladder does not hide it behind a disclosure the reviewer has to
        // remember to open.
        scope.ladderOpen = function(entry) {
          if (!entry) return false;

          return Boolean(
            entry.ladderMetric ||
              entry.stanceValue ||
              entry.fallbackValue ||
              entry.hardLimitValue,
          );
        };

        // What the customer said about giving this away, against what the
        // ledger recorded them doing.
        //
        // Reported and never corrected. The ledger is evidence, not a policy,
        // and a screen that quietly rewrote `last_resort` to `routine` because
        // the last five deals went the other way would be replacing a decision
        // with a measurement. `negotiated` is the middle and no rate
        // contradicts it, so it is left alone entirely.
        function costTensionFor(entry) {
          if (!entry || entry._duplicate || entry._costDismissed) return null;
          if (entry.state === 'no_position') return null;

          const cost = entry.concessionCost;
          if (cost !== 'routine' && cost !== 'last_resort') return null;

          const observed = entry.observed || emptyHistory();
          const deals = Number(observed.outcomeDeals) || 0;
          const conceded = Number(observed.changedIn) || 0;
          if (deals < COST_TENSION_MINIMUM_DEALS) return null;

          // A majority either way. Half is consistent with both readings and is
          // deliberately left silent — a prompt that fires on a coin flip is a
          // prompt people learn to dismiss.
          const mostly = conceded * 2 > deals;
          const rarely = conceded * 2 < deals;
          if (cost === 'last_resort' && !mostly) return null;
          if (cost === 'routine' && !rarely) return null;

          return { cost: cost, conceded: conceded, deals: deals };
        }

        // Stops the line coming back on the next refresh. Not persisted: a
        // customer who looks at the tension and keeps their answer has made a
        // decision about today's evidence, and next quarter's deals are allowed
        // to raise it again.
        scope.dismissCostTension = function(entry) {
          if (!entry) return;

          entry._costDismissed = true;
          scope.refreshDuplicates();
        };

        function historyFallbackFor(entry) {
          if (!entry || !entry.concept) return null;
          const proposal = (scope.proposals || {})[entry.concept];
          return proposal ? proposal.declared_fallback : null;
        }

        function historyEvidenceFor(entry) {
          if (!entry || !entry.concept) return null;
          const proposal = (scope.proposals || {})[entry.concept];
          return proposal ? proposal.proposal_evidence : null;
        }

        // Same landing, ignoring what no reviewer would call a difference.
        // Without this, a trailing period or a doubled space reads as a
        // conflict, and a prompt that cries wolf on punctuation trains people
        // to dismiss it without reading.
        function sameLanding(left, right) {
          const flatten = value =>
            String(value || '')
              .toLowerCase()
              .replace(/[\s\u00a0]+/g, ' ')
              .replace(/[.,;:]+$/g, '')
              .trim();
          return flatten(left) === flatten(right);
        }

        // Take what past deals landed on, over what the playbook already said.
        //
        // Not a promotion and not a demotion. An entry the reviewer had already
        // confirmed stays confirmed — choosing between two sources IS a
        // decision — and an entry still in draft stays in draft, because
        // picking a fallback is not the same as approving the position around
        // it. Source becomes `both`, which is the true answer: the position
        // came from one place and this rung from another.
        // One switch with two positions, rather than an action and a separate
        // undo. Either source can be put back at any time, so a reviewer can
        // read the field both ways before settling — which is the only way to
        // compare two wordings that are four lines long each.
        scope.chooseFallbackSource = function(entry, source) {
          if (!entry || entry._duplicate || !entry._historyFallback) return;

          // `proposalEvidence` is deliberately untouched. It renders under the
          // PREFERRED POSITION as what that draft rests on, and this switch is
          // about the fallback — writing the history evidence into it put a
          // sentence about the fallback in the stance's group, and printed it
          // twice, because the panel below already says the same thing beside
          // the value it actually describes.
          if (source === 'history') {
            entry.declaredFallback = entry._historyFallback;
            entry.source =
              entry.source === 'observed_history' ? 'observed_history' : 'both';
          } else {
            const original = entry._historyOriginal;
            if (!original) return;

            // The text and where it came from. Restoring the wording and
            // leaving `source` reading `both` would claim a provenance the
            // entry no longer has.
            entry.declaredFallback = original.fallback;
            entry.source = original.source;
          }

          // Either way this concept has been looked at, so it stops counting as
          // outstanding — while the switch itself stays put.
          entry._historyDecided = true;
          markDirty();
          scope.refreshDuplicates();
        };

        // The way in. Conflicts first: a written policy that no longer matches
        // what the company signs is worth more of the reviewer's attention than
        // a blank rung, and sending them to a gap while a contradiction waits
        // would bury the more expensive of the two.
        scope.showFirstHistoryOffer = function() {
          const rail = scope.rail || [];
          const match =
            rail.find(
              item => item.entry && item.entry._historyOffer === 'conflict',
            ) || rail.find(item => item.entry && item.entry._historyOffer);
          if (!match) return;

          scope.activeKey = match.key;
          resolveActive();
          showActiveFromTheTop();
        };

        // The form's own words, taken as a starting point. `formPosition` is
        // what the form SAYS — a factual statement about a document — so it
        // fills that field and nothing else. The declared ladder stays blank:
        // what the form currently says is not the same as what this company
        // wants, and pre-filling a preferred position from it would put a
        // policy in their mouth they never chose.
        // The ladder is filled now, where it used to be left deliberately blank.
        //
        // That rule was right when the only thing available was one quote from
        // the default render: what the form says today is not what this company
        // wants tomorrow. It is too cautious once the conditional variants are
        // read. A form that carries three versions of a clause is a record of
        // the range this company already lives with, and dropping it made the
        // reviewer re-derive a ladder from evidence they could not see.
        //
        // The rungs are the reader's INFERENCE — which variant is preferred and
        // which is the floor is not stated anywhere — so they arrive `proposed`
        // like everything else here, each carrying the section it was reasoned
        // from. Concepts whose clause offers no choice arrive with an empty
        // ladder, which is the correct answer rather than a gap.
        scope.useFormPosition = function(item) {
          if (!item || item.entry || !item.formPosition) return;

          const offer = item.formPosition;
          const entry = materialise(item.conceptId, 'proposed');
          entry.formPosition = offer.formPosition || undefined;
          entry.declaredStance = rungText(offer.preferredPosition);
          entry.declaredFallback = rungText(offer.fallback);
          entry.declaredHardLimit = rungText(offer.minimumAcceptable);
          entry.source = 'form';
          scope.refreshDuplicates();
        };

        function rungText(rung) {
          const text = rung && String(rung.text || '').trim();
          return text || undefined;
        }

        // Labelled with the words the reviewer's own fields use, not the
        // reader's field names. Precomputed per call rather than built in an
        // `ng-repeat` expression, which would re-run on every digest.
        const FORM_RUNGS = [
          { field: 'preferredPosition', label: 'Preferred position' },
          { field: 'fallback', label: 'Fallback' },
          { field: 'minimumAcceptable', label: 'Minimum acceptable' },
        ];

        // Number or title, whichever the extractor let through — it drops a
        // "title" that is really body text rather than pass it off as a section
        // name. Both gone means the source cannot be named, and naming it wrongly
        // is worse than not naming it.
        scope.formCitation = function(source) {
          if (!source) return null;

          return [source.sectionNumber, source.sectionTitle]
            .map(part => String(part || '').trim())
            .filter(Boolean)
            .join(' ') || null;
        };

        // Off scope for the same reason as `observedLandingsFor`. This one
        // predates that mistake and carried it quietly: it only ever ran while a
        // form position was on screen, so it was one rendered concept away from
        // aborting the digest on its own.
        function formOfferRungsFor(offer) {
          if (!offer) return [];

          return FORM_RUNGS.filter(rung => rungText(offer[rung.field])).map(function(rung) {
            const value = offer[rung.field];
            return {
              field: rung.field,
              label: rung.label,
              text: rungText(value),
              citation: scope.formCitation(value),
            };
          });
        }

        scope.declineConcept = function(item) {
          if (!item || item.entry) return;

          materialise(item.conceptId, 'no_position');
          // A decision, not the start of one. `materialise` pins the selection
          // to the concept it just wrote, which is right for its three other
          // callers — they create a DRAFT, and the reviewer needs to stay on it
          // to write the position. Declining is finished the moment it is
          // clicked, and the identical button on an entry has always moved on.
          // The queue's two exits behaved differently depending on whether the
          // concept happened to have a row yet.
          advancePastDecision(`concept:${item.conceptId}`);
        };

        // The bulk form of draftFromHistory, and it inherits that function's one
        // rule: the preferred position stays empty. History records what was
        // CONCEDED, so drafting a top rung from concessions would assert the
        // opposite of what the evidence says — and doing it forty times over
        // would do so invisibly.
        //
        // Everything written here is `proposed`. Nothing on this screen may
        // promote a position to `declared` without a person confirming it, and
        // a button that drafts in bulk is exactly where that line would go
        // missing.
        scope.draftAllFromHistory = function() {
          const draftable = scope.rail.filter(item => !item.entry && item.proposal);
          if (!draftable.length) return;

          draftable.forEach(scope.draftFromHistory);
          // materialise() moves the selection on every call, so without this the
          // reviewer lands on whichever concept happened to be drafted last.
          // The queue starts at its top.
          scope.activeKey = scope.railToDecide.length
            ? scope.railToDecide[0].key
            : scope.activeKey;
          resolveActive();
          beginReview('past decisions');
        };

        // The third way in. Not a dismissal of a prompt — the reviewer has
        // chosen to write the playbook by hand, and the rail is where that
        // happens.
        scope.startFromScratch = function() {
          scope.startDismissed = true;
          if (!scope.activeKey && scope.railToDecide.length) {
            scope.activeKey = scope.railToDecide[0].key;
            resolveActive();
          }
        };

        // A stack of past deals, one row each.
        //
        // Uploading is now fast: the request stores the documents, queues a row
        // and returns 202. The reading is minutes of work in a worker — a real
        // redline carries thousands of tracked changes and each classification
        // slice re-sends the whole executed lease — so the reviewer is told to
        // leave, and the rows are polled for as long as anyone is watching.
        //
        // A row's truth lives on the server. Once uploaded, a deal's status
        // comes from `past_redline_imports` and never from this screen, which is
        // what lets the tab be closed and reopened without losing the sitting.
        let nextDealId = 0;
        let pollHandle = null;

        scope.addRedlines = function(files) {
          Array.from(files || []).forEach(function(file) {
            nextDealId += 1;
            scope.redlineDeals.push({
              id: `deal-${nextDealId}`,
              redline: file,
              redlineFilename: file.name,
              // The answer key, optional per deal. A deal with one shows what
              // was conceded AND what was refused; a deal without still
              // contributes evidence, and the sample is reported as unable to
              // show refusals rather than as having found none.
              executed: null,
              executedFilename: null,
              importId: null,
              status: 'pending',
              error: null,
              result: null,
            });
          });
        };

        scope.attachExecuted = function(deal, file) {
          if (!deal || deal.status !== 'pending') return;
          deal.executed = file || null;
          deal.executedFilename = file ? file.name : null;
        };

        scope.removeRedlineDeal = function(deal) {
          if (!deal || deal.status !== 'pending') return;
          const index = scope.redlineDeals.indexOf(deal);
          if (index >= 0) scope.redlineDeals.splice(index, 1);
        };

        scope.clearRedlineDeals = function() {
          // No guard on `importingRedline`: that means the SERVER is reading,
          // not that the staging area is locked. A reviewer who adds more files
          // while a sitting is in flight can still change their mind about them.
          // Only the ones not yet sent. A queued or finished row is a record of
          // work the server is doing or has done, and clearing the screen must
          // not pretend otherwise.
          scope.redlineDeals = scope.redlineDeals.filter(
            deal => deal.status !== 'pending',
          );
          const input = root.querySelector('[data-redline-files]');
          if (input) input.value = '';
        };

        scope.pendingRedlines = function() {
          return scope.redlineDeals.filter(deal => deal.status === 'pending');
        };

        function unfinishedRedlines() {
          return scope.redlineDeals.filter(
            deal => deal.status === 'queued' || deal.status === 'reading',
          );
        }

        scope.unfinishedRedlines = unfinishedRedlines;

        // Rebuilt from the rows rather than accumulated as replies arrive: a
        // reviewer who reloads mid-sitting gets the same tally as one who
        // stayed, because both are reading the same server rows.
        function refreshRedlineSummary() {
          const done = scope.redlineDeals.filter(deal => deal.status === 'done');
          if (!done.length) {
            scope.redlineSummary = null;
            return;
          }

          scope.redlineSummary = {
            files: done.length,
            importedCount: done.reduce((sum, d) => sum + (d.result.importedCount || 0), 0),
            unclassifiedCount: done.reduce(
              (sum, d) => sum + (d.result.unclassifiedCount || 0),
              0,
            ),
            // False as soon as one deal lacked an answer key: the sample as a
            // whole then cannot speak to refusals, and saying otherwise would
            // overstate what the evidence supports.
            executedComparison: done.every(d => d.result.executedComparison),
          };
        }

        function applyImportRow(deal, row) {
          deal.status = row.status;
          deal.error = row.error || null;
          deal.redlineFilename = row.redlineFilename || deal.redlineFilename;
          deal.executedFilename = row.executedFilename || deal.executedFilename;
          if (row.status === 'done') {
            deal.result = {
              importedCount: row.importedCount || 0,
              unclassifiedCount: row.unclassifiedCount || 0,
              executedComparison: Boolean(row.executedComparison),
            };
          }
        }

        // Adopts rows this screen never staged, so a sitting survives a reload
        // and a second tab does not show a different story.
        function adoptImports(rows) {
          const byId = {};
          scope.redlineDeals.forEach(function(deal) {
            if (deal.importId) byId[deal.importId] = deal;
          });

          rows.forEach(function(row) {
            const known = byId[row.id];
            if (known) {
              applyImportRow(known, row);
              return;
            }
            if (row.status === 'done' || row.status === 'failed') return;

            nextDealId += 1;
            const adopted = {
              id: `deal-${nextDealId}`,
              redline: null,
              redlineFilename: row.redlineFilename,
              executed: null,
              executedFilename: row.executedFilename,
              importId: row.id,
              status: row.status,
              error: row.error || null,
              result: null,
            };
            scope.redlineDeals.push(adopted);
          });

          // What has already been read, counted from the server rather than
          // from this session. A finished import is deliberately not re-adopted
          // as a staged row — there is nothing left to wait for — but that left
          // the panel unable to account for itself: discard a draft, reopen it,
          // and "13 concepts have enough history" arrives from nowhere while
          // the negotiation that produced it is gone from the screen.
          scope.negotiationsRead = rows.filter(row => row.status === 'done').length;

          refreshRedlineSummary();
        }

        function stopPolling() {
          if (pollHandle) {
            $interval.cancel(pollHandle);
            pollHandle = null;
          }
        }

        function pollImports() {
          if (typeof ApiService.listPastRedlineImports !== 'function') {
            stopPolling();
            return Promise.resolve(null);
          }

          return ApiService.listPastRedlineImports(scope.documentType)
            .then(function(response) {
              const rows = (response.data || {}).imports || [];
              adoptImports(rows);

              if (unfinishedRedlines().length) return null;

              stopPolling();
              scope.importingRedline = false;
              // The ledger moved, so proposals and frequencies have to be
              // re-read. `keepDraft` is true because this touched no entry.
              if (scope.redlineSummary) return load(scope.documentType, true);
              return null;
            })
            .catch(function() {
              // A dropped poll is not a failed import: the worker is still
              // reading and the next tick will say so.
              return null;
            });
        }

        scope.pollImports = pollImports;

        // Never throws into the load chain. Whether a worker is still reading a
        // redline has nothing to do with whether the playbook can be shown, and
        // an unreachable or unavailable imports endpoint used to take the whole
        // screen down with it — entries never set, because the rejection landed
        // before `load`'s own handler.
        function resumeRedlineImports() {
          if (!scope.documentType) return null;

          try {
            return pollImports().then(function() {
              if (!unfinishedRedlines().length) return null;
              scope.importingRedline = true;
              startPolling();
              return null;
            });
          } catch (e) {
            return null;
          }
        }

        function startPolling() {
          if (pollHandle || typeof $interval !== 'function') return;
          // Six seconds: the work is minutes long, so anything faster is a
          // request per row for no new information.
          pollHandle = $interval(pollImports, 6000);
        }

        scope.importRedlines = function() {
          const queue = scope.pendingRedlines();
          // Guarded on a batch being SENT, not on the server having unfinished
          // work. Deals are independent jobs, so staging three more while two
          // are being read is a normal thing to do, and blocking it left the
          // submit button greyed out for a reason nobody could see.
          if (!scope.documentType || !queue.length || scope.redlineProgress)
            return;

          scope.error = null;
          scope.importingRedline = true;
          scope.redlineProgress = { done: 0, total: queue.length };

          // Asked once for the sitting. Twenty prompts to submit twenty deals is
          // the same permission twenty times.
          AiServicesConsentService.ensureAiServicesConsent().then(
            function() {
              return uploadDeal(queue, 0)
                .then(function() {
                  scope.redlineProgress = null;
                  // Once for the sitting, not once per deal. Twenty rows in the
                  // trail for one action would bury the six events that each
                  // mark a distinct decision, and the fact worth recording is
                  // that a body of history was brought in — not the order the
                  // files went up the wire.
                  audit(window.PlaybookNegotiationsImportedEvent, {
                    documentType: scope.documentType,
                    dealCount: queue.filter(deal => deal.status !== 'error').length,
                    failedCount: queue.filter(deal => deal.status === 'error').length,
                  });
                  if (unfinishedRedlines().length) {
                    startPolling();
                    return null;
                  }
                  scope.importingRedline = false;
                  return null;
                });
            },
            function() {
              scope.importingRedline = false;
              scope.redlineProgress = null;
              scope.error =
                'AI Services consent is required to read a past redline.';
            },
          );
        };

        // One at a time, and a failure does not stop the rest. These are byte
        // transfers now rather than whole readings, but a stack of twenty leases
        // is still a lot to put on the wire at once.
        function uploadDeal(queue, index) {
          if (index >= queue.length) return Promise.resolve();

          const deal = queue[index];
          deal.status = 'uploading';
          deal.error = null;
          scope.redlineProgress = { done: index, total: queue.length };

          return ApiService.importPastRedline({
            documentType: scope.documentType,
            redline: deal.redline,
            executed: deal.executed,
          })
            .then(function(response) {
              const data = response.data || {};
              deal.importId = data.importId;
              deal.status = data.status || 'queued';
              // The bytes are the server's now, and holding them here keeps a
              // whole lease in the tab for as long as the row is on screen.
              deal.redline = null;
              deal.executed = null;
            })
            .catch(function(error) {
              deal.status = 'error';
              deal.error = errorMessage(error, 'This redline could not be sent.');
            })
            .then(function() {
              scope.redlineProgress = { done: index + 1, total: queue.length };
              return uploadDeal(queue, index + 1);
            });
        }

        scope.showStart = function() {
          return Boolean(
            !scope.loading &&
              scope.documentType &&
              !scope.entries.length &&
              !scope.startDismissed,
          );
        };

        // Reading a form happens in the customer's own browser, because section
        // numbering is composed at render time by the list engine and exists
        // nowhere else — not in the template, and so not on the server. The
        // form manager is loaded hidden, `form_outline_harness` projects it once
        // it settles, and the outline comes back by postMessage.
        //
        // The iframe is a whole lease editor booting, so it is created on demand
        // and torn down the moment the outline arrives.
        let formFrame = null;
        let formListener = null;

        function teardownFormFrame() {
          if (formListener) {
            window.removeEventListener('message', formListener);
            formListener = null;
          }
          if (formFrame && formFrame.parentNode) {
            formFrame.parentNode.removeChild(formFrame);
          }
          formFrame = null;
        }

        // Whether the form picker is on screen. It is the way in before a
        // reading, and after one it is a second way to spend four minutes on a
        // question already answered — so it stands down in favour of taking the
        // positions, and comes back only when asked for.
        scope.pickingForm = function() {
          return scope.formStatus !== 'done' || Boolean(scope.choosingAnotherForm);
        };

        scope.chooseAnotherForm = function() {
          scope.choosingAnotherForm = true;
        };

        scope.selectedFormLabel = function() {
          const form = (scope.forms || []).find(
            candidate => String(candidate.id) === String(scope.picker.formId),
          );
          return form ? form.label : null;
        };

        scope.readForm = function() {
          if (!scope.picker.formId || scope.readingForm) return;

          scope.error = null;
          scope.choosingAnotherForm = false;
          scope.readingForm = true;
          scope.formStatus = 'rendering';
          startElapsed();
          teardownFormFrame();

          formListener = function(event) {
            // Same origin only, and only the message this asked for. Anything
            // else on the bus is somebody else's business.
            if (event.origin !== window.location.origin) return;
            const message = event.data || {};
            if (message.type !== 'lp:form-outline') return;
            if (String(message.formId) !== String(scope.picker.formId)) return;

            teardownFormFrame();
            scope.$apply(function() {
              if (message.error || !message.outline) {
                scope.readingForm = false;
                scope.formStatus = null;
                stopElapsed();
                scope.error = message.error || 'This form could not be read.';
                return;
              }
              submitFormOutline(message.outline);
            });
          };

          window.addEventListener('message', formListener);

          formFrame = document.createElement('iframe');
          formFrame.setAttribute('title', 'Reading your form');
          formFrame.setAttribute('aria-hidden', 'true');
          // Off-screen rather than display:none: a frame that is not laid out
          // does not render, and an unrendered form has no numbering, which is
          // the one thing this is here to collect.
          formFrame.style.cssText =
            'position:absolute;width:1200px;height:900px;left:-10000px;top:0;border:0;';
          formFrame.src = `/manage_forms/forms/${scope.picker.formId}/edit?playbook_outline=1`;
          document.body.appendChild(formFrame);
        };

        function submitFormOutline(outline) {
          scope.formStatus = 'sending';
          ApiService.importFormPositions({
            formId: scope.picker.formId,
            documentType: scope.documentType,
            outline: outline,
          })
            .then(function(response) {
              const data = response.data || {};
              scope.formImportId = data.importId;
              scope.formStatus = 'queued';
              scope.formSectionCount = data.sectionCount;
              startFormPolling();
            })
            .catch(function(error) {
              scope.readingForm = false;
              scope.formStatus = null;
              scope.error = errorMessage(error, 'This form could not be sent.');
            });
        }

        let formPollHandle = null;

        function stopFormPolling() {
          if (formPollHandle) {
            $interval.cancel(formPollHandle);
            formPollHandle = null;
          }
        }

        function pollFormImports() {
          if (typeof ApiService.listFormPositionImports !== 'function') {
            stopFormPolling();
            return Promise.resolve(null);
          }

          return ApiService.listFormPositionImports(scope.documentType)
            .then(function(response) {
              const rows = (response.data || {}).imports || [];
              const row = rows.find(item => item.id === scope.formImportId) || rows[0];
              if (!row) return null;

              scope.formStatus = row.status;
              if (row.status !== 'done' && row.status !== 'failed') return null;

              stopFormPolling();
              scope.readingForm = false;
              if (row.status === 'failed') {
                scope.error = row.error || 'This form could not be read.';
                return null;
              }
              scope.applyFinishedReading(row.positions);
              // On completion, not on submission. The fact worth recording is
              // that the form was read and how much of it landed — a queued job
              // that later failed would otherwise sit in the trail as though it
              // had done something.
              audit(window.PlaybookFormReadEvent, {
                documentType: scope.documentType,
                formId: scope.picker && scope.picker.formId,
                conceptCount: (row.positions || []).length,
              });
              return null;
            })
            .catch(function() {
              return null;
            });
        }

        // Offered, never applied. These are proposals against concepts the
        // reviewer has not looked at, and §8's silence-versus-miss distinction
        // only means anything if a person confirms.
        //
        // The rail is built when the playbook loads, long before a worker
        // finishes reading a form, so assigning the positions is not enough:
        // every item still holds `formPosition: null` and no offer would ever
        // appear. The reading would report "33 concepts, 32 with a position"
        // and change nothing a reviewer could act on.
        //
        // Its own function so a test can drive exactly what the poll drives.
        // The first version of that test assigned `formPositions` and called
        // `refreshDuplicates` itself, which is how this shipped broken.
        scope.applyFinishedReading = function(positions) {
          scope.formPositions = positions || [];
          scope.refreshDuplicates();
          resolveActive();
        };

        // What the reviewer has just been handed, and by what. The three
        // creators — a written playbook, past decisions, a form — all produce
        // the same thing: a queue of drafts a person must confirm or reject.
        // Only the reading REPORT differs, and that stays with the import.
        function beginReview(source) {
          scope.refreshDuplicates();
          scope.reviewBatch = scope.draftsWaiting;
          scope.reviewSource = source;
          // Persisted after the batch is known, not before. The creators write
          // their positions one at a time and each write persists, so without
          // this the saved draft carried the entries and a batch of zero — and
          // a restored queue came back with a progress bar reading no progress
          // against no denominator.
          persistDraft();
        }

        // Straight to the first thing that needs a decision. The rail is the only
        // way into the work otherwise, and a reviewer looking at 41 rows has no
        // reason to know which of them the import filled.
        scope.reviewNext = function() {
          const all = (scope.unclassifiedRail || []).concat(scope.rail || []);
          const next = firstNeedingAttention(all);
          if (!next) return;

          scope.startDismissed = true;
          scope.activeKey = next.key;
          resolveActive();
          showActiveFromTheTop();
        };

        scope.pollFormImports = pollFormImports;

        function startFormPolling() {
          if (formPollHandle || typeof $interval !== 'function') return;
          formPollHandle = $interval(pollFormImports, 6000);
        }

        // Take all of them, which is what reading a form is FOR. The other two
        // sources both do this — a written playbook drafts every position it
        // finds, past decisions have "Draft N positions" — and the form offered
        // only a count and a per-concept prompt, so a reviewer who had just
        // waited for 100 sections to be read had nothing to show for it until
        // they walked the queue.
        //
        // Same rule as taking one: `formPosition` is filled and the declared
        // ladder is left blank, because what the form says today is not what
        // this company wants tomorrow. Everything written is `proposed`.
        scope.draftAllFromForm = function() {
          const draftable = scope.rail.filter(item => !item.entry && item.formPosition);
          if (!draftable.length) return;

          draftable.forEach(scope.useFormPosition);
          // The queue starts at its top rather than wherever the last write
          // happened to leave the selection.
          if (scope.railToDecide.length) {
            scope.activeKey = scope.railToDecide[0].key;
            resolveActive();
          }
          // The plate has done its job; the work is in the queue now.
          scope.startDismissed = true;
          beginReview('your form');
        };

        scope.draftableFormPositions = function() {
          return (scope.rail || []).filter(item => !item.entry && item.formPosition).length;
        };

        scope.quotedFormPositions = function() {
          return (scope.formPositions || []).filter(position => position.formPosition);
        };

        scope.toggleSource = function(name) {
          scope.openSource = scope.openSource === name ? null : name;
        };

        // The way back to the three sources once the playbook has entries.
        // They lived only on the start plate, so a single entry put all of them
        // out of reach — and past negotiations is the one most worth reaching by
        // then: recorded outcomes are what the disagreement report measures
        // declared hard lines against, and there is nothing to measure until
        // positions exist.
        scope.toggleSources = function() {
          scope.sourcesOpen = !scope.sourcesOpen;
          // Closing the way in closes what it opened. Leaving a panel expanded
          // behind a collapsed section is how it reappears, mid-scroll, the next
          // time someone opens this.
          if (!scope.sourcesOpen) scope.openSource = null;
        };

        // The one writer. `documentType` stays the value every other function
        // reads; the select's copy is kept in step with it here, so a refused
        // switch puts the select back rather than leaving it naming a playbook
        // that was not loaded.
        function setDocumentType(type) {
          scope.documentType = type;
          scope.picker.documentType = type;
        }

        scope.chooseDocumentType = function(type) {
          if (!type || type === scope.documentType) {
            setDocumentType(scope.documentType);
            return;
          }
          setDocumentType(type);
          scope.changeDocumentType();
        };

        // Typing a stance is the one text edit that can change the disagreement
        // report, because `hardLinePattern` reads `declaredStance`. Bound to
        // that field alone rather than to `markDirty`, so the other three
        // textareas cost nothing.
        scope.refreshDisagreements = function() {
          scope.disagreementList = buildDisagreements();
        };

        scope.disagreementMessage = function(entry) {
          const observed = entry.observed || emptyHistory();
          return `You record this as never conceded; it has been conceded in ${observed.changedInRecent} of the last ${observed.recentOutcomeDeals}.`;
        };

        scope.rejectionReasonLabel = function(reason) {
          return rejectionReasonLabels[reason.code] || reason.code;
        };

        scope.refreshDuplicates = function() {
          const live = liveEntries();
          // A discarded duplicate is not a duplicate. Counting it would leave
          // Save disabled over a conflict the admin has already resolved.
          const counts = live.reduce(function(result, entry) {
            if (entry.concept)
              result[entry.concept] = (result[entry.concept] || 0) + 1;
            return result;
          }, {});
          scope.duplicateConcepts = Object.keys(counts).filter(
            conceptId => counts[conceptId] > 1,
          );
          scope.entries.forEach(function(entry) {
            entry._duplicate =
              !entry._discarded && !!entry.concept && counts[entry.concept] > 1;
            entry.observed =
              scope.historyByConcept[entry.concept] ||
              entry.observed ||
              emptyHistory();

            // What past deals landed on, held against what this entry already
            // says. `buildRail` drops the proposal the moment an entry exists,
            // which is right for the button that drafts into empty buckets and
            // wrong here: a bucket that is already full is exactly where the
            // two sources can contradict each other, and until now the reviewer
            // could read both and act on neither.
            const drafted = String(historyFallbackFor(entry) || '').trim();
            const declared = String(entry.declaredFallback || '').trim();
            entry._historyFallback = drafted || null;
            entry._historyEvidence = historyEvidenceFor(entry);

            // Which source the field is currently showing, read off the field
            // itself rather than held as a mode. A reviewer who edits the text
            // by hand afterwards is on neither, and the switch says so instead
            // of claiming a choice they did not make.
            const onHistory = Boolean(drafted) && sameLanding(drafted, declared);

            // The customer's own wording, kept fresh for as long as it is what
            // the field says. This is what "keep what the playbook says"
            // restores, and capturing it here rather than at the moment of
            // replacement means it survives an edit made before the switch was
            // ever touched.
            if (drafted && !onHistory)
              entry._historyOriginal = {
                fallback: entry.declaredFallback,
                source: entry.source,
              };

            entry._historySelection = null;
            // `no_position` is left alone. The reviewer has answered, and the
            // answer is that this concept has no ladder — offering to fill a
            // rung would be arguing with a decision rather than informing one.
            if (drafted && !entry._duplicate && entry.state !== 'no_position')
              entry._historySelection = onHistory ? 'history' : 'playbook';

            // What still needs settling, which is what the counts and the
            // "take me there" pointer are about. The switch above stays on
            // screen whatever this says — a disagreement does not stop being
            // true because somebody has looked at it — but it stops being
            // outstanding work once they have.
            // What still needs settling. The switch above stays on the card
            // whatever this says — a disagreement does not stop being true —
            // but a concept the reviewer has DECIDED is not outstanding work.
            //
            // `_historyDecided` alone was not enough: it is a session flag, and
            // a save reloads every entry from the server, so after committing a
            // finished playbook the pointer counted all of them again and told
            // a reviewer with nothing left to do that twelve concepts wanted
            // attention. A confirmed position was confirmed with the
            // disagreement on screen; that IS the decision.
            entry._historyOffer = null;
            if (
              entry._historySelection === 'playbook' &&
              !entry._historyDecided &&
              !DECIDED_STATES.includes(entry.state) &&
              !sameLanding(drafted, declared)
            )
              entry._historyOffer = declared ? 'conflict' : 'gap';

            entry._costTension = costTensionFor(entry);
            entry._observedLandings = observedLandingsFor(entry);
          });

          // Counted, because a contradiction buried on concept 31 of 41 may as
          // well not exist. Split in two: "past deals landed somewhere else"
          // and "past deals can fill a blank you left" are different asks and
          // adding them together produces a number that answers neither.
          scope.historyConflictCount = liveEntries().filter(
            entry => entry._historyOffer === 'conflict',
          ).length;
          scope.historyGapCount = liveEntries().filter(
            entry => entry._historyOffer === 'gap',
          ).length;
          scope.historyOfferCount =
            scope.historyConflictCount + scope.historyGapCount;

          // Stored rather than bound as functions: the headings and the footer
          // read these every digest, and the disagreement filter alone runs a
          // regex per classified entry on each one.
          scope.classifiedCount = live.filter(entry => !!entry.concept).length;
          scope.unclassifiedCount = live.filter(entry => !entry.concept).length;
          // The workload an import creates, counted live rather than held from
          // the import: confirming one takes it out, discarding one takes it
          // out, and a reviewer coming back tomorrow sees what is actually left
          // rather than what arrived.
          scope.draftsWaiting = live.filter(
            entry => entry.state === 'proposed',
          ).length;
          // Against what the import actually returned, so the bar measures the
          // job the reviewer was handed. A position discarded rather than
          // decided still counts as dealt with — it is gone from the queue and
          // nothing is waiting on it.
          // Against the batch the reviewer was handed, whatever handed it to
          // them. This used to read `importSummary.entryCount`, which tied the
          // whole progress panel to ONE of the three things that create a queue
          // of drafts — so drafting 13 positions from past decisions, or taking
          // what a form says, produced the same work with none of the machinery
          // that explains it.
          const batch = scope.reviewBatch || 0;
          scope.reviewedCount = Math.max(0, batch - scope.draftsWaiting);
          scope.reviewedPercent = batch
            ? Math.round((scope.reviewedCount / batch) * 100)
            : 0;
          scope.discardedCount = scope.entries.length - live.length;

          refreshLists();
          scope.filterConcepts();
        };

        scope.discardEntry = function(entry) {
          entry._discarded = true;
          markDirty();
          scope.refreshDuplicates();
        };

        // Undoes the discard and, when the discard came from a merge, the merge
        // with it. `_mergeUndo` holds the target's field values from before the
        // concatenation, because `"A\n\nB"` does not say where the boundary was
        // — it is indistinguishable from a stance somebody typed as two
        // paragraphs, so nothing downstream could have split it back.
        scope.restoreEntry = function(entry) {
          if (entry._mergeUndo) {
            const target = scope.entries.find(
              candidate =>
                candidate._draftId === entry._mergeUndo.targetDraftId,
            );

            if (target) {
              MERGEABLE_FIELDS.forEach(function(field) {
                target[field] = entry._mergeUndo.before[field];
              });
              target.sourceCount = entry._mergeUndo.targetSourceCount;
              target.source = entry._mergeUndo.targetSource;
            }

            delete entry._mergeUndo;
          }

          entry._discarded = false;
          markDirty();
          scope.refreshDuplicates();
        };

        scope.mergeIntoFirst = function(entry) {
          // Among live entries only: with discards now reversible, a discarded
          // duplicate is still in the array and must not be merged into.
          const target = liveEntries().find(
            candidate => candidate.concept === entry.concept,
          );
          if (!target || target === entry) return;

          const before = {};
          MERGEABLE_FIELDS.forEach(function(field) {
            before[field] = target[field];
          });

          MERGEABLE_FIELDS.forEach(function(field) {
            const incoming = entry[field];
            if (!incoming) return;
            if (!target[field]) target[field] = incoming;
            else if (target[field] !== incoming)
              target[field] += `\n\n${incoming}`;
          });

          entry._mergeUndo = {
            targetDraftId: target._draftId,
            targetSourceCount: target.sourceCount,
            targetSource: target.source,
            before: before,
          };

          target.sourceCount =
            (target.sourceCount || 1) + (entry.sourceCount || 1);
          target.source =
            target.source === entry.source ? target.source : 'merged';

          entry._discarded = true;
          markDirty();
          scope.refreshDuplicates();
        };

        scope.canMergeIntoFirst = function(entry) {
          return (
            liveEntries().find(
              candidate => candidate.concept === entry.concept,
            ) !== entry
          );
        };

        // Saving nothing is not saving — the server destroys every entry and
        // recreates what it was sent, so an empty request wipes the playbook.
        // Harmless when the playbook is genuinely empty and destructive when
        // the load failed, and those two look identical on screen.
        //
        // Deleting a playbook deliberately is a different act and should have
        // its own affordance rather than being reachable by pressing Save on a
        // screen that failed to load.
        scope.canCommit = function() {
          return Boolean(
            scope.documentType &&
              !scope.saving &&
              // Nothing to write, and writing anyway is not free: `commit`
              // destroys and recreates every row, so a no-op save re-stamps
              // `confirmed_by_user_id` and `confirmed_at` on positions nobody
              // opened — the audit answer to "who confirmed this" becomes
              // whoever clicked Save last. The controller already accepts that
              // for entries a reviewer really did re-commit; there is no reason
              // to hand it out for a click that changes nothing.
              scope.dirty &&
              !scope.duplicateConcepts.length &&
              liveEntries().length,
          );
        };

        scope.commit = function() {
          scope.refreshDuplicates();
          if (!scope.canCommit()) return;

          scope.error = null;
          scope.saving = true;
          ApiService.commitPlaybook({
            documentType: scope.documentType,
            // Discarded entries are still in `scope.entries` so they can be
            // restored; they must never reach the commit boundary.
            entries: liveEntries(),
          })
            .then(function(response) {
              scope.savedSummary = response.data;
              // On success only. A save that failed changed no policy, and an
              // audit trail recording intent rather than effect is worse than
              // none — somebody reading it later cannot tell which is which.
              audit(window.PlaybookSavedEvent, {
                documentType: scope.documentType,
                declaredCount: (response.data && response.data.declaredCount) || 0,
                draftCount: (response.data && response.data.draftCount) || 0,
              });
              scope.importSummary = null;
              if (window.localStorage)
                window.localStorage.removeItem(draftKey());
              scope.draftRestoredAt = null;
              return load(scope.documentType, false);
            })
            .catch(function(error) {
              scope.error = errorMessage(
                error,
                'The playbook could not be saved.',
              );
            })
            .finally(function() {
              scope.saving = false;
            });
        };

        // One delegated handler for all four file inputs on this screen — the
        // written playbook on the plate, the same control in the chrome row, and
        // the redline pair. Each is inside an ng-if, so binding to the elements
        // themselves would mean rebinding every time the screen advances.
        function handleFilesChanged(event) {
          const target = event.target;
          if (!target || !target.matches) return;

          if (target.matches('[data-playbook-files]')) {
            scope.$apply(function() {
              scope.dropError = null;
              scope.files = Array.from(target.files || []);
            });
          } else if (target.matches('[data-redline-files]')) {
            scope.$apply(function() {
              scope.addRedlines(target.files);
            });
            // Emptied so choosing the same file again still fires a change and
            // still adds a row — a customer re-picking after removing one by
            // mistake would otherwise get nothing.
            target.value = '';
          } else if (target.matches('[data-executed-file]')) {
            // Each row owns its own input, so the row is named on the element
            // rather than tracked in a variable the delegation cannot see.
            const dealId = target.getAttribute('data-deal-id');
            const deal = scope.redlineDeals.find(item => item.id === dealId);
            scope.$apply(function() {
              scope.attachExecuted(deal, (target.files || [])[0] || null);
            });
          }
        }

        // The converter reads tracked changes and section numbering out of the
        // OOXML, so a PDF or a screenshot is not a slow path — it is nothing to
        // read at all.
        const WORD_DOCUMENT = /\.docx?$/i;

        // Delegated for the same reason `change` is: a zone is two `ng-if`s deep
        // and does not exist at link time, and the per-deal ones are created and
        // destroyed as rows are staged and sent.
        //
        // Three zones, three different things a drop MEANS, which is why the
        // attribute carries a kind rather than just marking the element:
        //
        //   files     — the written playbook. REPLACES the chosen set. One
        //               import reads one policy document.
        //   redlines  — the stack of past negotiations. APPENDS a row per file,
        //               because this panel is explicitly a bulk operation.
        //   executed  — one deal's executed version. ATTACHES to the row named
        //               on the element, and takes exactly one file.
        //
        // Getting these backwards is silent and expensive: a replacing redline
        // drop would discard a stack somebody spent a minute assembling.
        const dropZone = node =>
          node && node.closest ? node.closest('[data-playbook-drop]') : null;

        const dropKind = zone => zone && zone.getAttribute('data-playbook-drop');

        // Locked for a reason the reviewer can see, per zone. `redlineProgress`
        // means the browser is releasing bytes deal by deal, and restaging
        // underneath that is what makes a queue and a screen disagree.
        function dropRefused(zone) {
          if (scope.importing || scope.saving) return true;
          return dropKind(zone) !== 'files' && Boolean(scope.redlineProgress);
        }

        function handleDragOver(event) {
          const zone = dropZone(event.target);
          if (!zone || dropRefused(zone)) return;

          // Without this the browser leaves the page to display the dropped
          // file, taking an unsaved playbook with it. `dragover` is the event
          // that has to be cancelled, not `drop` — a drop is only delivered
          // over an element that already refused the default.
          event.preventDefault();
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
          // Named, not a flag: with three zones on screen a boolean lit all of
          // them at once, so a file held over one deal's row highlighted every
          // other row and the stack above them.
          const id = zone.getAttribute('data-deal-id') || dropKind(zone);
          if (scope.droppingOn === id) return;
          scope.$apply(function() {
            scope.droppingOn = id;
          });
        }

        function handleDragLeave(event) {
          if (!scope.droppingOn || !dropZone(event.target)) return;
          // `dragleave` fires on every child the pointer crosses on its way
          // across the zone, so the highlight is dropped only once the pointer
          // has actually left it.
          if (dropZone(event.relatedTarget)) return;
          scope.$apply(function() {
            scope.droppingOn = null;
          });
        }

        function handleDrop(event) {
          const zone = dropZone(event.target);
          if (!zone) return;
          event.preventDefault();
          if (dropRefused(zone)) return;

          const kind = dropKind(zone);
          const dropped = Array.from(
            (event.dataTransfer && event.dataTransfer.files) || [],
          ).filter(file => WORD_DOCUMENT.test(file.name));

          scope.$apply(function() {
            scope.droppingOn = null;
            if (!dropped.length) {
              scope.dropError =
                'That is not a Word document. The reader needs a .docx.';
              return;
            }
            scope.dropError = null;

            if (kind === 'redlines') {
              scope.addRedlines(dropped);
            } else if (kind === 'executed') {
              const deal = scope.redlineDeals.find(
                item => item.id === zone.getAttribute('data-deal-id'),
              );
              // One row holds one executed version, so a multi-file drop on a
              // row takes the first rather than silently attaching the last.
              scope.attachExecuted(deal, dropped[0]);
            } else {
              scope.files = dropped;
            }
          });

          // The input still holds whatever was last chosen through it. Left
          // set, choosing that same file again fires no `change`, so a drop
          // followed by re-picking the same file through the dialog does
          // nothing at all.
          clearFileInput(kind === 'files' ? findFileInput() : null);
          if (kind !== 'files') {
            clearFileInput(root.querySelector('[data-redline-files]'));
          }
        }

        function clearFileInput(input) {
          if (input) input.value = '';
        }

        // The way back out. Choosing the wrong document used to be recoverable
        // only by reopening the OS dialog, which is a strange thing to have to
        // do to undo a click.
        scope.removeFile = function(index) {
          if (scope.importing || scope.saving) return;

          scope.files = scope.files.filter(
            (_file, position) => position !== index,
          );
          scope.dropError = null;
          // Cleared so re-choosing the file just removed still fires a
          // `change`. Without it a mistaken Remove is unrepeatable through the
          // dialog.
          const fileInput = findFileInput();
          if (fileInput) fileInput.value = '';
        };

        // Nothing on this screen is persisted before Save — no draft, no
        // localStorage, no server-side copy — so a stray back button, a nav
        // link, a reload or a closed tab took the whole session: every field
        // typed, every concept assigned, every merge decision, and the import
        // itself. Two guards, because they catch different exits.
        function handleBeforeUnload(event) {
          if (!scope.dirty) return undefined;

          // Browsers ignore custom text here and show their own wording; the
          // assignment is what makes the prompt appear at all.
          event.preventDefault();
          event.returnValue = '';
          return '';
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        const cancelLocationGuard = scope.$root.$on(
          '$locationChangeStart',
          function(event) {
            if (!scope.dirty) return;
            if (
              !window.confirm(
                'You have unsaved playbook changes. Leaving this page discards them.',
              )
            ) {
              event.preventDefault();
            }
          },
        );

        // `change` does not bubble in the DOM spec but DOES in every browser
        // implementation, and the listener is on the directive's own root, so a
        // capture-phase listener is not needed to reach it.
        root.addEventListener('change', handleFilesChanged);
        root.addEventListener('dragover', handleDragOver);
        root.addEventListener('dragleave', handleDragLeave);
        root.addEventListener('drop', handleDrop);

        scope.$on('$destroy', function() {
          stopPolling();
          stopFormPolling();
          teardownFormFrame();
          root.removeEventListener('change', handleFilesChanged);
          root.removeEventListener('dragover', handleDragOver);
          root.removeEventListener('dragleave', handleDragLeave);
          root.removeEventListener('drop', handleDrop);
          document.removeEventListener('keydown', handleShortcut);
          window.removeEventListener('beforeunload', handleBeforeUnload);
          cancelLocationGuard();
        });

        load(null, false);
      },
    };
  },
]);
